import React, { useEffect, useState, useRef, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './MapView.css';
import axios from 'axios';
import Modal from 'react-modal';
import {
  FaDrawPolygon,
  FaRegSquare,
  FaRulerHorizontal,
  FaInfoCircle,
  FaLocationArrow,
  FaSearch,
  FaTimes,
  FaUserCircle
} from 'react-icons/fa';
import * as turf from '@turf/turf';
import tawanaiLogo from './assets/Tawanai_logo.png';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import ProgressBar from './ProgressBar';

function filterPointsForAll(pointsData, activeConditions, columnBins = {}) {
  if (!pointsData?.features) return pointsData;
  if (!activeConditions.length) return { ...pointsData, features: [] };

  // Build: condType => set of checked values
  const condMap = {};
  activeConditions.forEach(key => {
    const [condType, condVal] = key.split(':');
    if (!condType || condVal === undefined || condVal === null) return;
    if (!condMap[condType]) condMap[condType] = new Set();
    condMap[condType].add(condVal.trim());
  });

  // Get all unique values for each column
  const columnAllValues = {};
  Object.keys(conditionColors).forEach(condType => {
    columnAllValues[condType] = new Set(Object.keys(conditionColors[condType]));
  });
  binColumns.forEach(col => {
    columnAllValues[col] = new Set((columnBins[col] || []).map(b => b.label));
  });

  // Filtering logic
  return {
    ...pointsData,
    features: pointsData.features.filter(f => {
      // For each filter column, only filter if not all values are checked
      for (const condType in condMap) {
        const checkedVals = condMap[condType];
        const allVals = columnAllValues[condType];
        if (checkedVals.size === 0 || checkedVals.size === allVals.size) continue; // skip filter if all checked

        let featureVal;
        if (binColumns.includes(condType)) {
          const val = Number(f.properties[condType]);
          const bins = columnBins[condType];
          featureVal = null;
          if (bins) {
            const matchedBin = bins.find(bin => val >= bin.min && val < (bin.max ?? Infinity));
            featureVal = matchedBin ? matchedBin.label : null;
          }
        } else {
          featureVal = typeof f.properties[condType] === "string"
            ? f.properties[condType].trim()
            : String(f.properties[condType]).trim();
        }
        if (!checkedVals.has(featureVal)) {
          return false;
        }
      }
      return true;
    }),
  };
}

Modal.setAppElement('#root');

mapboxgl.accessToken = 'pk.eyJ1Ijoia3V6ZWUyMiIsImEiOiJjbWRmeWVldnAwaHBxMmxzOTVkdHBmcmJ2In0.Gl01FteWfpVPXMZzE_VcFA';

const defaultScores = {
  "Slope Level": { "<5": 10, "5-10": 9, "10-15": 8, "15-25": 6, ">25": 0 },
  "Distance from Water Bodies(in km)": { "<20": 8, "20-40": 6, "40-60": 4, "60-80": 2, ">80": 0 },
  "LULC Scenarios": { "Barren Land": 6, "Crop Lands": 10, "Forests": 8, "Mixed Land": 5, "No-Go Zones": 0 },
  "Distance from Cities(in km)": { "<20": 8, "20-40": 6, "40-60": 4, "60-80": 2, ">80": 0 },
  "Distance from PT Lines(in km)": { "<20": 8, "20-40": 6, "40-60": 4, "60-80": 2, ">80": 0 },
  "Distance from Roads(in km)": { "<20": 8, "20-40": 6, "40-60": 4, "60-80": 2, ">80": 0 },
};

const conditionColors = {
  "Slope Level": { "<5": "#F28F3B", "5-10": "#E4572E", "10-15": "#4E79A7", "15-25": "#888888", ">25": "#0000FF" },
  "Distance from Water Bodies(in km)": { "<20": "#F28F3B", "20-40": "#E4572E", "40-60": "#4E79A7", "60-80": "#888888", ">80": "#0000FF" },
  "LULC Scenarios": { "Barren Land": "#E2C290", "Crop Lands": "#A1C349", "Forests": "#9F3C8C", "Mixed Land": "#888888", "No-Go Zones": "#F28F3B" },
  "Distance from Cities(in km)": { "<20": "#F28F3B", "20-40": "#E4572E", "40-60": "#4E79A7", "60-80": "#888888", ">80": "#0000FF" },
  "Distance from PT Lines(in km)": { "<20": "#F28F3B", "20-40": "#E4572E", "40-60": "#4E79A7", "60-80": "#888888", ">80": "#0000FF" },
  "Distance from Roads(in km)": { "<20": "#F28F3B", "20-40": "#E4572E", "40-60": "#4E79A7", "60-80": "#888888", ">80": "#0000FF" }
};

const binColumns = [
  "Wind Speed(m/s)-100m",
  "Qualitative Score",
  "Quantitative Score",
  "LCOE Values",
  "Final Score"
];
const binColors = ["#F28F3B", "#E4572E", "#4E79A7", "#888888", "#0000FF"]; // 5 bins
const windSpeedColors = ["#FFFFCC", "#FFB3FF", "#00FFFF", "#00FF00", "#FFA500"];
const CUSTOM_LAYER_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'];

function buildColorExpression(viewBy, columnBins, activeConditions) {
  if (viewBy === "All") return 'black';
  if (binColumns.includes(viewBy)) {
    return ['case', ...((columnBins[viewBy] || []).flatMap((bin, idx) => {
      const color = viewBy === "Wind Speed(m/s)-100m"
        ? windSpeedColors[idx % windSpeedColors.length]
        : binColors[idx % binColors.length];
      return [
        ['all', ['>=', ['to-number', ['get', viewBy]], bin.min], ['<', ['to-number', ['get', viewBy]], bin.max ?? Infinity]],
        color,
      ];
    })), ['rgba', 0, 0, 0, 0]];
  } else if (conditionColors[viewBy]) {
    const entries = Object.entries(conditionColors[viewBy]).filter(([c]) => activeConditions.includes(c));
    return entries.length > 0
      ? ['match', ['get', viewBy], ...entries.flat(), ['rgba', 0, 0, 0, 0]]
      : ['match', ['get', viewBy], '___DUMMY___', ['rgba', 0, 0, 0, 0], ['rgba', 0, 0, 0, 0]];
  }
  return ['rgba', 0, 0, 0, 0];
}

function getNumericBins(min, max, bins = 5) {
  if (min == null || max == null || min === max) return [];
  const step = (max - min) / bins;
  const result = [];
  for (let i = 0; i < bins; i++) {
    result.push({
      label: (min + i * step).toFixed(2) + " - " + (min + (i + 1) * step).toFixed(2),
      min: min + i * step,
      max: min + (i + 1) * step
    });
  }
  // Ensure last bin covers the max exactly
  if (result.length) result[result.length - 1].max = max;
  return result;
}

function findBinLabel(value, bins) {
  for (let bin of bins) {
    if (value >= bin.min && value <= bin.max) return bin.label;
  }
  return null;
}
// Use shared styles for modal
function EditProfileModal({ onClose, onProfileUpdated, onLogout }) {
  const [tab, setTab] = useState('username');
  const [newUsername, setNewUsername] = useState('');
  const [usernameMsg, setUsernameMsg] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [deleteMsg, setDeleteMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const buttonStyle = {
    padding: '9px 18px',
    marginRight: 8,
    background: 'linear-gradient(135deg, #1e3a8a, #2563eb)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
    boxShadow: '0 2px 10px rgba(37,99,235,0.28)',
    transition: 'all 0.2s',
  };
  const buttonSecondary = {
    ...buttonStyle,
    background: '#f1f5f9',
    color: '#0f172a',
    border: '1.5px solid #e2e8f0',
    boxShadow: 'none',
  };
  

  const axiosConfig = {
    headers: {
      'Authorization': 'Bearer ' + localStorage.getItem('token')
    }
  };

  const handleUsernameChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/change-username/`,
        { username: newUsername, password: currentPassword },
        axiosConfig
      );
      setUsernameMsg('Username updated!');
      localStorage.setItem('username', newUsername);
      if (onProfileUpdated) onProfileUpdated(localStorage.getItem('first_name'), newUsername);
    } catch (err) {
      setUsernameMsg(err.response?.data?.error || 'Error updating username');
    }
    setLoading(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/change-password/`,
        { old_password: currentPassword, new_password: newPassword },
        axiosConfig
      );
      setPasswordMsg('Password updated!');
    } catch (err) {
      setPasswordMsg(err.response?.data?.error || 'Error updating password');
    }
    setLoading(false);
  };

  const handleDeleteProfile = async () => {
    if (!window.confirm('Are you sure you want to delete your profile? This cannot be undone.')) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/delete-profile/`, {}, axiosConfig);
      setDeleteMsg('Profile deleted');
      if (onLogout) onLogout();
    } catch (err) {
      setDeleteMsg(err.response?.data?.error || 'Error deleting profile');
    }
    setLoading(false);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(15,23,42,0.35)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 3003,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '14px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.14)',
          border: '1px solid #e8edf3',
          padding: '36px',
          minWidth: '420px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
        }}
        onClick={e => e.stopPropagation()}
      >
                {/* Tabs */}
                <div style={{ display: 'flex', marginBottom: 24, width: '100%', justifyContent: 'center' }}>
          <button
            onClick={() => setTab('username')}
            style={{
              ...buttonSecondary,
              marginRight: 8,
              background: tab === 'username' ? '#eff6ff' : buttonSecondary.background,
              borderColor: tab === 'username' ? '#bfdbfe' : '#e2e8f0',
              color: tab === 'username' ? '#1e3a8a' : '#0f172a',
              fontWeight: tab === 'username' ? 700 : 600,
            }}
            type="button"
          >
            Change Username
          </button>
          <button
            onClick={() => setTab('password')}
            style={{
              ...buttonSecondary,
              marginRight: 8,
              background: tab === 'password' ? '#eff6ff' : buttonSecondary.background,
              borderColor: tab === 'password' ? '#bfdbfe' : '#e2e8f0',
              color: tab === 'password' ? '#1e3a8a' : '#0f172a',
              fontWeight: tab === 'password' ? 700 : 600,
            }}
            type="button"
          >
            Change Password
          </button>
          <button
            onClick={() => setTab('delete')}
            style={{
              ...buttonSecondary,
              background: tab === 'delete' ? '#fef2f2' : buttonSecondary.background,
              borderColor: tab === 'delete' ? '#fecaca' : '#e2e8f0',
              color: tab === 'delete' ? '#dc2626' : '#0f172a',
              fontWeight: tab === 'delete' ? 700 : 600,
            }}
            type="button"
          >
            Delete Profile
          </button>
        </div>
        {/* Forms */}
        {tab === 'username' && (
        <form onSubmit={handleUsernameChange} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <input
            placeholder="New Username"
            value={newUsername}
            onChange={e => setNewUsername(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #ccc',
              marginBottom: '12px',
              fontSize: '15px'
            }}
          />
          <input
            type="password"
            placeholder="Current Password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #ccc',
              marginBottom: '12px',
              fontSize: '15px'
            }}
          />
          <button type="submit" disabled={loading} style={{ ...buttonStyle, width: '100%', marginRight: 0 }}>
            {loading ? 'Changing...' : 'Change Username'}
          </button>
        </div>
        {usernameMsg && <div style={{ marginTop: 10, color: usernameMsg.includes('updated') ? '#059669' : '#dc2626', fontWeight: 500 }}>{usernameMsg}</div>}
          </form>
        )}
        {tab === 'password' && (
        <form onSubmit={handlePasswordChange} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <input
            type="password"
            placeholder="Current Password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #ccc',
              marginBottom: '12px',
              fontSize: '15px'
            }}
          />
          <input
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #ccc',
              marginBottom: '12px',
              fontSize: '15px'
            }}
          />
          <button type="submit" disabled={loading} style={{ ...buttonStyle, width: '100%', marginRight: 0 }}>
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </div>
        {passwordMsg && <div style={{ marginTop: 10, color: passwordMsg.includes('updated') ? '#059669' : '#dc2626', fontWeight: 500 }}>{passwordMsg}</div>}
          </form>
        )}
        {tab === 'delete' && (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <p style={{ color: '#E4572E', fontWeight: 600, marginBottom: 16 }}>Delete your profile permanently.</p>
        <button
          onClick={handleDeleteProfile}
          disabled={loading}
          style={{
            ...buttonSecondary,
            color: '#fff',
            background: 'linear-gradient(135deg, #b91c1c, #dc2626)',
            border: 'none',
            boxShadow: '0 2px 10px rgba(220,38,38,0.28)',
            width: '100%',
            marginRight: 0
          }}
        >
          {loading ? 'Deleting...' : 'Delete Profile'}
        </button>
        {deleteMsg && <div style={{ marginTop: 10, color: deleteMsg.includes('deleted') ? '#059669' : '#dc2626', fontWeight: 500 }}>{deleteMsg}</div>}
</div>
        )}
        <button
          onClick={onClose}
          style={{
            ...buttonSecondary,
            marginTop: 24,
            width: '100%',
            marginRight: 0
          }}
          type="button"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function createSubstationBadge() {
  const W = 40, H = 24, R = 5;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Rounded-rect background with a subtle top-to-bottom gradient
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, '#FCD34D');
  grad.addColorStop(1, '#F59E0B');
  ctx.beginPath();
  ctx.moveTo(R, 0); ctx.lineTo(W - R, 0);
  ctx.arcTo(W, 0, W, R, R); ctx.lineTo(W, H - R);
  ctx.arcTo(W, H, W - R, H, R); ctx.lineTo(R, H);
  ctx.arcTo(0, H, 0, H - R, R); ctx.lineTo(0, R);
  ctx.arcTo(0, 0, R, 0, R); ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  // Thin white border
  ctx.strokeStyle = 'rgba(255,255,255,0.75)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // "SS" label
  ctx.fillStyle = '#1C1917';
  ctx.font = 'bold 13px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('SS', W / 2, H / 2);

  // Return explicit { width, height, data } — passing the canvas element directly
  // causes a "mismatched image size" error in Mapbox when it infers dimensions internally.
  const { data } = ctx.getImageData(0, 0, W, H);
  return { width: W, height: H, data };
}

function formatMapScale(zoom, latitude) {
  const lat = Number.isFinite(latitude) ? latitude : 28;
  const metersPerPixel = 156543.03392 * Math.cos((lat * Math.PI) / 180) / Math.pow(2, zoom || 0);
  const denominator = metersPerPixel / 0.000264583;
  if (!Number.isFinite(denominator) || denominator <= 0) return '1:--';
  if (denominator >= 1000000) return `1:${(denominator / 1000000).toFixed(1)}M`;
  if (denominator >= 1000) return `1:${Math.round(denominator / 1000)}k`;
  return `1:${Math.round(denominator)}`;
}

function formatInspectorValue(value, digits = 3) {
  if (value === undefined || value === null || value === '') return 'N/A';
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(digits) : String(value);
}

const MapView = ({ onLogout }) => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [region, setRegion] = useState("Northern Algeria"); // "Global Algeria" or "Northern Algeria"
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false); 
  const [firstName, setFirstName] = useState(localStorage.getItem('first_name') || "");
  const [userName, setUserName] = useState(localStorage.getItem('username') || "");
  const [scoreMap, setScoreMap] = useState(defaultScores);
  // For "All" mode, activeConditions is an array of ${condType}:${cond} strings; for others, it's just [cond]
  const [viewBy, setViewBy] = useState("Wind Speed(m/s)-100m");
  const [activeConditions, setActiveConditions] = useState([]);
  const [panelTab, setPanelTab] = useState('layers');
  const [guideLang, setGuideLang] = useState('en');
  const [scoringDraft, setScoringDraft] = useState(defaultScores);
  const [scoringSaving, setScoringsSaving] = useState(false);
  const [scoringMsg, setScoringMsg] = useState('');
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [algeriaData, setAlgeriaData] = useState(null);
  const [pointsData, setPointsData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressVisible, setProgressVisible] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState('Loading data…');
  const [searchLocId, setSearchLocId] = useState('');
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v12');
  const mapStyleRef = useRef('mapbox://styles/mapbox/streets-v12');
  const [selectedPoints, setSelectedPoints] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [drawActive, setDrawActive] = useState(false);
  const [selectMode, setSelectMode] = useState('polygon'); // 'polygon' | 'box'
  const [boxRect, setBoxRect] = useState(null); // { left, top, width, height } in px
  const boxStartRef = useRef(null); // { x, y } container-relative px
  const drawRef = useRef(null);
  const selectModeRef = useRef('polygon');
  const drawActiveRef = useRef(false);
  const [distancePoints, setDistancePoints] = useState([null, null]);
  const [distanceKm, setDistanceKm] = useState(null);
  const activeConditionsRef = useRef([]);
  const filteredPointsRef = useRef(null);
  const [showSubstations, setShowSubstations] = useState(false);
  const [substationsData, setSubstationsData] = useState(null);
  const [substationsStatus, setSubstationsStatus] = useState('idle'); // 'idle' | 'loading' | 'error'
  const substationsDataRef = useRef(null);
  const showSubstationsRef = useRef(false);
  const selectedPointsRef = useRef(null);
  const [customLayers, setCustomLayers] = useState([]);
  const customLayersRef = useRef([]);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(true);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [inspectorFeature, setInspectorFeature] = useState(null);
  const [mapStatus, setMapStatus] = useState({
    zoom: 6.56,
    lng: 4.0435,
    lat: 34.8659,
    scale: formatMapScale(6.56, 34.8659),
  });
  const handleLocIdSearch = (e) => {
    e.preventDefault();
    if (!searchLocId || !pointsData?.features) return;
    const found = pointsData.features.find(f =>
      f.properties.name?.toString().toLowerCase() === searchLocId.toLowerCase()
    );
    if (found && map.current) {
      const [lon, lat] = found.geometry.coordinates;
      map.current.flyTo({ center: [lon, lat], zoom: 12, essential: true });
      setInspectorFeature(found);
      setInspectorOpen(true);
      if (window.__locIdMarker) window.__locIdMarker.remove();
      window.__locIdMarker = new mapboxgl.Marker({ color: 'red' })
        .setLngLat([lon, lat])
        .addTo(map.current);
      setTimeout(() => {
        if (window.__locIdMarker) {
          window.__locIdMarker.remove();
          window.__locIdMarker = null;
        }
      }, 3000);
    } else {
      alert('Loc ID not found!');
    }
  };

  function getConditionKeysForFeatures(features, columnBins) {
    const keys = new Set();
    features.forEach(f => {
      // Categorical
      Object.keys(conditionColors).forEach(condType => {
        const val = f.properties[condType];
        if (val && val !== "NA") keys.add(`${condType}:${val}`);
      });
      // Binned numeric
      binColumns.forEach(col => {
        const v = Number(f.properties[col]);
        const bins = columnBins[col] || [];
        const bin = bins.find(b => v >= b.min && v < (b.max ?? Infinity));
        if (bin) keys.add(`${col}:${bin.label}`);
      });
    });
    return Array.from(keys);
  }

  
  function getFeatureBinLabel(feature, col) {
    const val = Number(feature.properties[col]);
    return findBinLabel(val, columnBins[col] || []);
  }

  const axiosConfig = {
    headers: {
      'Authorization': 'Bearer ' + localStorage.getItem('token')
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const cfg = {
      headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') },
      signal: controller.signal,
    };
    setLoadingLabel(map.current ? 'Switching region…' : 'Loading data…');
    setProgressVisible(true);
    setProgress(10);

    Promise.all([
      axios.get(`${API_URL}/api/algeria/`, cfg),
      axios.get(`${API_URL}/api/points/?region=${encodeURIComponent(region)}`, cfg),
    ]).then(([algRes, ptsRes]) => {
      setProgress(80);
      setAlgeriaData(algRes.data);
      setPointsData(ptsRes.data);
      setProgress(100);
      setTimeout(() => setProgressVisible(false), 400);
    }).catch(err => {
      if (!axios.isCancel(err)) console.error('Failed to load map data:', err);
      setProgressVisible(false);
    });

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  useEffect(() => {
    if (!map.current) return;
    if (region === 'Global Algeria') {
      map.current.flyTo({ center: [2.0, 28.5], zoom: 4.39, essential: true });
    } else {
      map.current.flyTo({ center: [4.0435, 34.8659], zoom: 6.56, essential: true });
    }
  }, [region]);

  useEffect(() => {
    setSubstationsStatus('loading');
    axios.get(`${API_URL}/api/substations/`, {
      headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
    }).then(res => {
      substationsDataRef.current = res.data;
      setSubstationsData(res.data);
      setSubstationsStatus('idle');
    }).catch(err => {
      console.error('[Substations] fetch failed:', err);
      setSubstationsStatus('error');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { showSubstationsRef.current = showSubstations; }, [showSubstations]);
  useEffect(() => { customLayersRef.current = customLayers; }, [customLayers]);
  useEffect(() => { selectedPointsRef.current = selectedPoints; }, [selectedPoints]);
  useEffect(() => { activeConditionsRef.current = activeConditions; }, [activeConditions]);
  useEffect(() => { selectModeRef.current = selectMode; }, [selectMode]);
  useEffect(() => { drawActiveRef.current = drawActive; }, [drawActive]);

  // Clear distance state when leaving distance mode
  useEffect(() => {
    if (selectMode !== 'distance') {
      setDistancePoints([null, null]);
      setDistanceKm(null);
    }
  }, [selectMode]);

  // Calculate distance when both points are set
  useEffect(() => {
    const [a, b] = distancePoints;
    if (a && b) {
      const d = turf.distance(turf.point([a.lng, a.lat]), turf.point([b.lng, b.lat]), { units: 'kilometers' });
      setDistanceKm(d);
    } else {
      setDistanceKm(null);
    }
  }, [distancePoints]);

  // Update distance line/markers on map
  useEffect(() => {
    if (!map.current) return;
    const src = map.current.getSource('distance-line');
    if (!src) return;
    const [a, b] = distancePoints;
    const features = [];
    if (a) features.push(turf.point([a.lng, a.lat], { label: 'A' }));
    if (b) features.push(turf.point([b.lng, b.lat], { label: 'B' }));
    if (a && b) features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: [[a.lng, a.lat], [b.lng, b.lat]] }, properties: {} });
    src.setData({ type: 'FeatureCollection', features });
  }, [distancePoints]);

  // Distance mode map click handler
  useEffect(() => {
    if (!map.current || selectMode !== 'distance' || !drawActive) return;
    map.current.getCanvas().style.cursor = 'crosshair';
    function handleDistanceClick(e) {
      const { lng, lat } = e.lngLat;
      setDistancePoints(prev => {
        if (!prev[0] || (prev[0] && prev[1])) return [{ lng, lat }, null];
        return [prev[0], { lng, lat }];
      });
    }
    map.current.on('click', handleDistanceClick);
    return () => {
      if (map.current) {
        map.current.off('click', handleDistanceClick);
        map.current.getCanvas().style.cursor = '';
      }
    };
  }, [selectMode, drawActive]);

  useEffect(() => {
    if (!map.current) return;
    const applyWorkspaceLayout = () => {
      if (!map.current) return;
      map.current.resize();
      map.current.setPadding({
        top: 86,
        left: leftPanelCollapsed ? 82 : 310,
        right: inspectorOpen ? 350 : 18,
        bottom: 74,
      });
    };

    applyWorkspaceLayout();
    const resizeTimer = window.setTimeout(applyWorkspaceLayout, 120);
    return () => window.clearTimeout(resizeTimer);
  }, [leftPanelCollapsed, inspectorOpen]);

  const [columnBins, setColumnBins] = useState({});

  const customBins = {
    "Wind Speed(m/s)-100m": [
      { label: "0 - 6", min: 0, max: 6},
      { label: "6 - 6.5", min: 6, max: 6.5 },
      { label: "6.5 - 7", min: 6.5, max: 7 },
      { label: "7 - 7.5", min: 7, max: 7.5 },
      { label: ">7.5", min: 7.5, max: Infinity },
    ],
    "Qualitative Score": [
      { label: "<0.25", min: 0.0, max: 0.25 },
      { label: "0.25 - 0.5", min: 0.25, max: 0.5 },
      { label: "0.5 - 0.75", min: 0.5, max: 0.75 },
      { label: ">0.75", min: 0.75, max: Infinity },
    ],
    "Quantitative Score": [
      { label: "<0.25", min: 0.0, max: 0.25 },
      { label: "0.25 - 0.5", min: 0.25, max: 0.5 },
      { label: "0.5 - 0.75", min: 0.5, max: 0.75 },
      { label: ">0.75", min: 0.75, max: Infinity },
    ],
    "LCOE Values": [
      { label: "<0.25", min: 0.0, max: 0.25 },
      { label: "0.25 - 0.5", min: 0.25, max: 0.5 },
      { label: "0.5 - 0.75", min: 0.5, max: 0.75 },
      { label: ">0.75", min: 0.75, max: Infinity },
    ],
    "Final Score": [
      { label: "0 - 0.5", min: 0, max: 0.5 },
      { label: "0.5 - 0.6", min: 0.5, max: 0.6 },
      { label: "0.6 - 0.7", min: 0.6, max: 0.7 },
      { label: "0.7 - 0.8", min: 0.7, max: 0.8 },
      { label: ">0.8", min: 0.8, max: Infinity },
    ]
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (viewBy === "All" && pointsData?.features) {
      const allKeys = getConditionKeysForFeatures(pointsData.features, columnBins);
      setActiveConditions(allKeys);
    }
    else if (defaultScores[viewBy]) {
      setActiveConditions(Object.keys(defaultScores[viewBy]));
    } else if (binColumns.includes(viewBy) && columnBins[viewBy]) {
      // Only activate bins that actually have matching points
      const validKeys = columnBins[viewBy]
      .filter(bin => pointsData?.features?.some(f => {
        const v = Number(f.properties[viewBy]);
        return v >= bin.min && v < (bin.max ?? Infinity);
      }))
      // .map(bin => ${viewBy}:${bin.label});
      .map(bin => bin.label);
      setActiveConditions(validKeys);
    } else {
      setActiveConditions([]);
    }
  }, [viewBy, pointsData, columnBins]);
  
  useEffect(() => {
    if (
      map.current ||
      !algeriaData ||
      !pointsData ||
      (viewBy === "Wind Speed(m/s)-100m" &&
        (!columnBins["Wind Speed(m/s)-100m"] ||
          columnBins["Wind Speed(m/s)-100m"].length === 0))
    ) {
      return;
    }
  
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      center: [4.0435, 34.8659],
      zoom: 6.56,
    });
  
    map.current.on('load', () => {
      map.current.resize();
      map.current.setPadding({ top: 86, left: 310, right: 18, bottom: 74 });
      // Add Algeria source/layer
      map.current.addSource('algeria', { type: 'geojson', data: algeriaData });
      map.current.addLayer({
        id: 'algeria-layer',
        type: 'fill',
        source: 'algeria',
        paint: { 'fill-color': '#000000', 'fill-opacity': 0 },
      });
  
      // Add points source/layer
      const currentConditions = activeConditionsRef.current;
      const filteredPoints = viewBy === "All"
        ? pointsData
        : filterPointsForAll(pointsData, currentConditions, columnBins);

      map.current.addSource('points', { type: 'geojson', data: filteredPoints });

      let expression;
      if (viewBy === "All") {
        expression = 'black';
      } else if (binColumns.includes(viewBy)) {
        expression = [
          'case',
          ...((columnBins[viewBy] || []).flatMap((bin, idx) => {
            const color = viewBy === "Wind Speed(m/s)-100m"
              ? windSpeedColors[idx % windSpeedColors.length]
              : binColors[idx % binColors.length];

            return [
              ['all',
                ['>=', ['to-number', ['get', viewBy]], bin.min],
                ['<', ['to-number', ['get', viewBy]], bin.max ?? Infinity]
              ],
              color
            ];
          })),
          ['rgba', 0, 0, 0, 0]
        ];
      } else {
        const filteredEntries = Object.entries(conditionColors[viewBy])
          .filter(([cond]) => currentConditions.includes(cond));
        expression =
          filteredEntries.length > 0
            ? ['match', ['get', viewBy], ...filteredEntries.flat(), ['rgba', 0, 0, 0, 0]]
            : ['match', ['get', viewBy], '___DUMMY___', ['rgba', 0, 0, 0, 0], ['rgba', 0, 0, 0, 0]];
      }
      map.current.addLayer({
        id: 'points-layer',
        type: 'circle',
        source: 'points',
        paint: {
          'circle-radius': 2,
          'circle-color': expression
        }
      });

      const draw = new MapboxDraw({ displayControlsDefault: false, controls: {} });
      map.current.addControl(draw);
      drawRef.current = draw;

      map.current.addSource('selected-points', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.current.addLayer({
        id: 'selected-points-layer',
        type: 'circle',
        source: 'selected-points',
        paint: {
          'circle-radius': 4,
          'circle-color': buildColorExpression(viewBy, columnBins, activeConditionsRef.current),
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
        }
      });

      map.current.addSource('hover-point', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.current.addLayer({
        id: 'points-hover-layer',
        type: 'circle',
        source: 'hover-point',
        paint: {
          'circle-radius': 7,
          'circle-color': 'rgba(255,255,255,0.15)',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        }
      });

      map.current.addSource('distance-line', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.current.addLayer({
        id: 'distance-line-layer',
        type: 'line',
        source: 'distance-line',
        filter: ['==', ['geometry-type'], 'LineString'],
        paint: { 'line-color': '#2563eb', 'line-width': 2.5, 'line-dasharray': [4, 3] },
      });
      map.current.addLayer({
        id: 'distance-markers-layer',
        type: 'circle',
        source: 'distance-line',
        filter: ['==', ['geometry-type'], 'Point'],
        paint: { 'circle-radius': 8, 'circle-color': '#2563eb', 'circle-stroke-width': 3, 'circle-stroke-color': '#ffffff' },
      });
      map.current.addLayer({
        id: 'distance-labels-layer',
        type: 'symbol',
        source: 'distance-line',
        filter: ['==', ['geometry-type'], 'Point'],
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 12,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-offset': [0, -1.6],
          'text-anchor': 'bottom',
        },
        paint: {
          'text-color': '#1e40af',
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
        },
      });

      const openInspectorForFeature = (feature) => {
        if (selectModeRef.current === 'distance') return;
        if (!feature) return;
        setInspectorFeature({
          type: feature.type,
          geometry: feature.geometry,
          properties: { ...(feature.properties || {}) },
        });
        setInspectorOpen(true);
      };
      map.current.on('click', 'points-layer', (e) => openInspectorForFeature(e.features?.[0]));
      map.current.on('click', 'points-hover-layer', (e) => openInspectorForFeature(e.features?.[0]));
      map.current.on('click', 'selected-points-layer', (e) => openInspectorForFeature(e.features?.[0]));

      const updateMapStatus = (lngLat) => {
        if (!map.current) return;
        const center = map.current.getCenter();
        const zoom = map.current.getZoom();
        const coords = lngLat || center;
        setMapStatus({
          zoom,
          lng: coords.lng,
          lat: coords.lat,
          scale: formatMapScale(zoom, center.lat),
        });
      };
      updateMapStatus();
      map.current.on('mousemove', (e) => updateMapStatus(e.lngLat));
      map.current.on('moveend', () => updateMapStatus());
      map.current.on('zoomend', () => updateMapStatus());

      map.current.addImage('substation-badge', createSubstationBadge());
      map.current.addSource('substations', {
        type: 'geojson',
        data: substationsDataRef.current || { type: 'FeatureCollection', features: [] }
      });
      map.current.addLayer({
        id: 'substations-layer',
        type: 'symbol',
        source: 'substations',
        layout: {
          visibility: 'none',
          'icon-image': 'substation-badge',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.45, 7, 0.7, 11, 1],
        },
        paint: {}
      });
      map.current.on('click', 'substations-layer', (e) => {
        if (drawActiveRef.current) return;
        const props = e.features[0].properties;
        const coords = e.features[0].geometry.coordinates.slice();
        const name = props.name || 'Unnamed Substation';
        const voltage = props.voltage ? `${props.voltage} V` : 'Unknown';
        const operator = props.operator || 'Unknown';
        new mapboxgl.Popup()
          .setLngLat(coords)
          .setHTML(`<div class="mv-popup-card"><div class="mv-popup-id">⚡ ${name}</div><div class="mv-popup-grid"><div class="mv-popup-item"><span class="mv-popup-key">Voltage</span><span class="mv-popup-val">${voltage}</span></div><div class="mv-popup-item"><span class="mv-popup-key">Operator</span><span class="mv-popup-val">${operator}</span></div></div></div>`)
          .addTo(map.current);
      });
      map.current.on('mouseenter', 'substations-layer', () => {
        if (drawActiveRef.current) return;
        map.current.getCanvas().style.cursor = 'pointer';
      });
      map.current.on('mouseleave', 'substations-layer', () => {
        if (drawActiveRef.current) return;
        map.current.getCanvas().style.cursor = '';
      });

      map.current.on('draw.create', (e) => {
        if (!e.features?.length) return;
        const polygon = e.features[0];
        const candidates = [
          ...(filteredPointsRef.current?.features || []),
          ...(showSubstationsRef.current ? (substationsDataRef.current?.features || []).filter(f => f.geometry?.type === 'Point') : []),
          ...customLayersRef.current.flatMap(l => l.visible ? (l.geojson?.features || []).filter(f => f.geometry?.type === 'Point') : []),
        ];
        const inside = candidates.filter(f => turf.booleanPointInPolygon(f, polygon));
        drawRef.current.deleteAll();
        setDrawActive(false);
        if (inside.length > 0) {
          setSelectedPoints({ type: 'FeatureCollection', features: inside });
        }
      });
    });

  }, [algeriaData, pointsData, columnBins, viewBy, activeConditions]);

  useEffect(() => {
    if (!map.current || !algeriaData || !pointsData) return;
    if (mapStyleRef.current === mapStyle) return;
    mapStyleRef.current = mapStyle;

    // Drain any in-flight GeoJSON worker events before the style tears down,
    // otherwise Map._render races against the half-removed layer manager.
    const subSrc = map.current.getSource('substations');
    if (subSrc) subSrc.setData({ type: 'FeatureCollection', features: [] });
    customLayersRef.current.forEach(layer => {
      const src = map.current.getSource(layer.id);
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
    });

    map.current.setStyle(mapStyle);
    map.current.once('style.load', () => {
      map.current.addSource('algeria', { type: 'geojson', data: algeriaData });
      map.current.addLayer({ id: 'algeria-layer', type: 'fill', source: 'algeria', paint: { 'fill-color': '#000000', 'fill-opacity': 0 } });

      const fp = viewBy === "All"
        ? pointsData
        : filterPointsForAll(pointsData, activeConditions, columnBins);
      map.current.addSource('points', { type: 'geojson', data: fp });

      let expr;
      if (viewBy === "All") {
        expr = 'black';
      } else if (binColumns.includes(viewBy)) {
        expr = ['case', ...((columnBins[viewBy] || []).flatMap((bin, idx) => {
          const color = viewBy === "Wind Speed(m/s)-100m"
            ? windSpeedColors[idx % windSpeedColors.length]
            : binColors[idx % binColors.length];
          return [['all', ['>=', ['to-number', ['get', viewBy]], bin.min], ['<', ['to-number', ['get', viewBy]], bin.max ?? Infinity]], color];
        })), ['rgba', 0, 0, 0, 0]];
      } else {
        const entries = Object.entries(conditionColors[viewBy] || {}).filter(([c]) => activeConditions.includes(c));
        expr = entries.length > 0
          ? ['match', ['get', viewBy], ...entries.flat(), ['rgba', 0, 0, 0, 0]]
          : ['rgba', 0, 0, 0, 0];
      }
      map.current.addLayer({ id: 'points-layer', type: 'circle', source: 'points', paint: { 'circle-radius': 2, 'circle-color': expr } });
      const existingSel = selectedPointsRef.current;
      map.current.addSource('selected-points', {
        type: 'geojson',
        data: (existingSel?.features?.length > 0) ? existingSel : { type: 'FeatureCollection', features: [] }
      });
      map.current.addLayer({
        id: 'selected-points-layer',
        type: 'circle',
        source: 'selected-points',
        paint: {
          'circle-radius': 4,
          'circle-color': buildColorExpression(viewBy, columnBins, activeConditions),
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
        }
      });

      map.current.addSource('hover-point', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.current.addLayer({
        id: 'points-hover-layer',
        type: 'circle',
        source: 'hover-point',
        paint: {
          'circle-radius': 7,
          'circle-color': 'rgba(255,255,255,0.15)',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        }
      });

      // Restore points-layer visibility to match the active selection
      if (existingSel?.features?.length > 0 && map.current.getLayer('points-layer')) {
        map.current.setLayoutProperty('points-layer', 'visibility', 'none');
      }

      if (!map.current.hasImage('substation-badge')) {
        map.current.addImage('substation-badge', createSubstationBadge());
      }
      map.current.addSource('substations', {
        type: 'geojson',
        data: substationsDataRef.current || { type: 'FeatureCollection', features: [] }
      });
      map.current.addLayer({
        id: 'substations-layer',
        type: 'symbol',
        source: 'substations',
        layout: {
          visibility: showSubstationsRef.current ? 'visible' : 'none',
          'icon-image': 'substation-badge',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.45, 7, 0.7, 11, 1],
        },
        paint: {}
      });
      // Distance tool layers
      const [dA, dB] = distancePoints;
      const distFeatures = [];
      if (dA) distFeatures.push(turf.point([dA.lng, dA.lat], { label: 'A' }));
      if (dB) distFeatures.push(turf.point([dB.lng, dB.lat], { label: 'B' }));
      if (dA && dB) distFeatures.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: [[dA.lng, dA.lat], [dB.lng, dB.lat]] }, properties: {} });
      map.current.addSource('distance-line', { type: 'geojson', data: { type: 'FeatureCollection', features: distFeatures } });
      map.current.addLayer({ id: 'distance-line-layer', type: 'line', source: 'distance-line', filter: ['==', ['geometry-type'], 'LineString'], paint: { 'line-color': '#2563eb', 'line-width': 2.5, 'line-dasharray': [4, 3] } });
      map.current.addLayer({ id: 'distance-markers-layer', type: 'circle', source: 'distance-line', filter: ['==', ['geometry-type'], 'Point'], paint: { 'circle-radius': 8, 'circle-color': '#2563eb', 'circle-stroke-width': 3, 'circle-stroke-color': '#ffffff' } });
      map.current.addLayer({ id: 'distance-labels-layer', type: 'symbol', source: 'distance-line', filter: ['==', ['geometry-type'], 'Point'], layout: { 'text-field': ['get', 'label'], 'text-size': 12, 'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'], 'text-offset': [0, -1.6], 'text-anchor': 'bottom' }, paint: { 'text-color': '#1e40af', 'text-halo-color': '#ffffff', 'text-halo-width': 2 } });

      // Click/hover handlers are registered once in map.on('load') and survive setStyle —
      // do NOT re-register here or they accumulate on every style switch.

      // Re-add any user-uploaded overlay layers
      customLayersRef.current.forEach(layer => addCustomLayerToMap(layer));
    });
  }, [mapStyle, algeriaData, pointsData, viewBy, activeConditions, columnBins]);

  useEffect(() => {
    if (!map.current) return;
    // Update Algeria source
    if (algeriaData && map.current.getSource('algeria')) {
      map.current.getSource('algeria').setData(algeriaData);
    }
    // Update Points source
    if (pointsData && map.current.getSource('points')) {
      const filteredPoints = viewBy === "All"
        ? pointsData
        : filterPointsForAll(pointsData, activeConditions, columnBins);
      map.current.getSource('points').setData(filteredPoints);
    }
  }, [algeriaData, pointsData, viewBy, activeConditions, columnBins]);

  useEffect(() => {
    if (!map.current || !substationsData) return;
    const src = map.current.getSource('substations');
    if (src) src.setData(substationsData);
  }, [substationsData]);

  useEffect(() => {
    if (!map.current || !map.current.getLayer('substations-layer')) return;
    map.current.setLayoutProperty('substations-layer', 'visibility', showSubstations ? 'visible' : 'none');
  }, [showSubstations]);

  useEffect(() => {
    if (!map.current) return;
    customLayers.forEach(layer => addCustomLayerToMap(layer));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customLayers]);

  // Proximity-based tooltip logic
  useEffect(() => { 
    if (!map.current || !pointsData) return;
  
    if (
      viewBy === "Wind Speed(m/s)-100m" &&
      (!columnBins["Wind Speed(m/s)-100m"] || columnBins["Wind Speed(m/s)-100m"].length === 0)
    ) {
      return;
    }
  
    // --- Proximity Tooltip handler ---
    const emptyFC = { type: 'FeatureCollection', features: [] };
    let debounceTimer = null;

    // Use queryRenderedFeatures (GPU-indexed) instead of iterating all features with turf.
    function handleProximityTooltip(e) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (!map.current) return;
        const hits = map.current.queryRenderedFeatures(
          [[e.point.x - 8, e.point.y - 8], [e.point.x + 8, e.point.y + 8]],
          { layers: ['points-layer'] }
        );
        if (hits.length > 0) {
          map.current.getCanvas().style.cursor = 'pointer';
          const src = map.current.getSource('hover-point');
          if (src) src.setData({ type: 'FeatureCollection', features: [hits[0]] });
        } else {
          map.current.getCanvas().style.cursor = '';
          const src = map.current.getSource('hover-point');
          if (src) src.setData(emptyFC);
        }
      }, 40);
    }

    function handleZoom() {
      const src = map.current?.getSource('hover-point');
      if (src) src.setData(emptyFC);
    }

    map.current.on('mousemove', handleProximityTooltip);
    map.current.on('zoomstart', handleZoom);

    return () => {
      clearTimeout(debounceTimer);
      if (!map.current) return;
      map.current.off('mousemove', handleProximityTooltip);
      map.current.off('zoomstart', handleZoom);
    };
  }, [viewBy, pointsData, columnBins, scoreMap]);

  useEffect(() => { setScoringDraft(scoreMap); }, [scoreMap]);

    useEffect(() => {
      if (!map.current || !pointsData) return;
      const layer = map.current.getLayer('points-layer');
      if (!layer) return;
      // Filter points data if "All", otherwise show all points
      if (viewBy === "All") {
        // Show all points (no filter!)
        map.current.setFilter('points-layer', null);
      } else {
        if (binColumns.includes(viewBy)) {
          // Build OR of all selected bins' value ranges
          const binFilters = (columnBins[viewBy] || [])
            .filter(bin => activeConditions.includes(bin.label))
            .map(bin => ['all',
              ['>=', ['to-number', ['get', viewBy]], bin.min],
              ['<', ['to-number', ['get', viewBy]], bin.max ?? Infinity]
            ]);
          // If nothing selected, hide all; else OR them together
          map.current.setFilter('points-layer',
            binFilters.length === 0 ? ['==', ['get', viewBy], '__NONE__'] :
            binFilters.length === 1 ? binFilters[0] :
            ['any', ...binFilters]
          );
        } else {
          map.current.setFilter('points-layer', [
            'in', viewBy, ...activeConditions
          ]);
        }}
    
        let expression;
        if (viewBy === "All") {
          // All: Black points (original design)
          expression = 'black';
        } else if (binColumns.includes(viewBy)) {
          // Bin columns: use windSpeedColors for wind speed, else binColors
          expression = [
            'case',
            ...((columnBins[viewBy] || []).flatMap((bin, idx) => {
              const color = viewBy === "Wind Speed(m/s)-100m"
                ? windSpeedColors[idx % windSpeedColors.length]
                : binColors[idx % binColors.length];
              return [
                ['all',
                  ['>=', ['to-number', ['get', viewBy]], bin.min],
                  ['<', ['to-number', ['get', viewBy]], bin.max ?? Infinity]
                ],
                color
              ];
            })),
            ['rgba', 0, 0, 0, 0]
          ];
        } else if (conditionColors[viewBy]) {
          // Categorical: color by your mapping
          const filteredEntries = Object.entries(conditionColors[viewBy])
            .filter(([cond]) => activeConditions.includes(cond));
          expression =
            filteredEntries.length > 0
              ? ['match', ['get', viewBy], ...filteredEntries.flat(), ['rgba', 0, 0, 0, 0]]
              : ['match', ['get', viewBy], '___DUMMY___', ['rgba', 0, 0, 0, 0], ['rgba', 0, 0, 0, 0]];
        } else {
          // Fallback: transparent
          expression = ['rgba', 0, 0, 0, 0];
        }
      map.current.setPaintProperty('points-layer', 'circle-color', expression);
    }, [viewBy, activeConditions, scoreMap, pointsData]);

    

    useEffect(() => {
      if (!pointsData?.features) return;
      const newScoreMap = {};
      const conditionTypeToScoreField = {
        "Slope Level": "Slope score",
        "Distance from Water Bodies(in km)": "WaterBody score",
        "LULC Scenarios": "LULC score",
        "Distance from Cities(in km)": "City score",
        "Distance from PT Lines(in km)": "PT Line score",
        "Distance from Roads(in km)": "Road score",
      };
      [
        "Slope Level",
        "Distance from Water Bodies(in km)",
        "LULC Scenarios",
        "Distance from Cities(in km)",
        "Distance from PT Lines(in km)",
        "Distance from Roads(in km)"
      ].forEach(condType => {
    
        pointsData.features.forEach(feature => {
          const props = feature.properties;
          const condValue = props[condType];
          const scoreField = conditionTypeToScoreField[condType];
          const scoreValue = props[scoreField];
          if (condValue && scoreValue !== undefined && scoreValue !== null) {
            if (!newScoreMap[condType]) newScoreMap[condType] = {};
            newScoreMap[condType][condValue] = scoreValue;
          }
        });
      });
      setScoreMap(newScoreMap);
    }, [pointsData]);

    useEffect(() => {
      if (!pointsData?.features) return;
      pointsData.features.forEach(f => {
        if (f.properties["Wind Speed(m/s)"] !== undefined) {
          f.properties["Wind Speed(m/s)-100m"] = f.properties["Wind Speed(m/s)"];
        }
      });
    }, [pointsData]);

    useEffect(() => {
      if (
        viewBy === "All" &&
        pointsData?.features?.length > 0 &&
        Object.keys(columnBins).length > 0
      ) {
        // Collect ALL condition keys that actually exist in the data
        const allKeys = [];
        // Categorical columns
        Object.keys(conditionColors).forEach(condType => {
          const vals = new Set(pointsData.features.map(f => f.properties[condType]));
          vals.forEach(val => {
            if (val && val !== "NA") allKeys.push(`${condType}:${val}`);
          });
        });
        // Bin columns
        binColumns.forEach(col => {
          (columnBins[col] || []).forEach(bin => {
            allKeys.push(col + ':' + bin.label);
          });
        });
        setActiveConditions(allKeys);
      }
      // Only run this when viewBy or data changes
    }, [viewBy, pointsData, columnBins]);

    useEffect(() => {
      if (!pointsData?.features) return;
      const stats = {};
      const binsObj = {};
      binColumns.forEach(col => {
        const values = pointsData.features
          .map(f => Number(f.properties[col]))
          .filter(v => !isNaN(v) && v !== null && v !== undefined);
        if (values.length > 0) {
          const min = Math.min(...values);
          const max = Math.max(...values);
          stats[col] = { min, max };
          binsObj[col] = customBins[col] || getNumericBins(min, max, 5);
        } else {
          stats[col] = { min: null, max: null };
          binsObj[col] = [];
        }
      });
      setColumnBins(binsObj);
    }, [pointsData]);

  function addCustomLayerToMap(layer) {
    if (!map.current) return;
    if (map.current.getSource(layer.id)) return;
    map.current.addSource(layer.id, { type: 'geojson', data: layer.geojson });
    const vis = layer.visible ? 'visible' : 'none';
    map.current.addLayer({
      id: `${layer.id}-fill`,
      type: 'fill',
      source: layer.id,
      layout: { visibility: vis },
      paint: { 'fill-color': layer.color, 'fill-opacity': 0.25 },
    });
    map.current.addLayer({
      id: `${layer.id}-line`,
      type: 'line',
      source: layer.id,
      layout: { visibility: vis },
      paint: { 'line-color': layer.color, 'line-width': 1.8 },
    });
    map.current.addLayer({
      id: `${layer.id}-circle`,
      type: 'circle',
      source: layer.id,
      filter: ['any', ['==', ['geometry-type'], 'Point'], ['==', ['geometry-type'], 'MultiPoint']],
      layout: { visibility: vis },
      paint: { 'circle-radius': 5, 'circle-color': layer.color, 'circle-stroke-width': 1, 'circle-stroke-color': '#fff' },
    });
  }

  function removeCustomLayerFromMap(id) {
    if (!map.current) return;
    [`${id}-fill`, `${id}-line`, `${id}-circle`].forEach(lid => {
      if (map.current.getLayer(lid)) map.current.removeLayer(lid);
    });
    if (map.current.getSource(id)) map.current.removeSource(id);
  }

  async function handleCustomLayerUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const name = file.name.replace(/\.(kml|kmz)$/i, '');
    const ext = file.name.split('.').pop().toLowerCase();
    try {
      let kmlText;
      if (ext === 'kmz') {
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(file);
        const kmlEntry = Object.values(zip.files).find(f => f.name.endsWith('.kml'));
        if (!kmlEntry) { alert('No KML file found inside the KMZ archive.'); return; }
        kmlText = await kmlEntry.async('text');
      } else {
        kmlText = await file.text();
      }
      const { kml: togeojsonKml } = await import('@tmcw/togeojson');
      const dom = new DOMParser().parseFromString(kmlText, 'text/xml');
      const geojson = togeojsonKml(dom);
      if (!geojson.features?.length) { alert('No features found in the uploaded file.'); return; }
      const id = `custom-${Date.now()}`;
      const color = CUSTOM_LAYER_COLORS[customLayersRef.current.length % CUSTOM_LAYER_COLORS.length];
      const layer = { id, name, visible: true, geojson, color };
      setCustomLayers(prev => [...prev, layer]);
    } catch (err) {
      console.error('[CustomLayer] parse error:', err);
      alert('Failed to parse the uploaded file. Make sure it is a valid KML or KMZ.');
    }
    e.target.value = '';
  }

  function toggleCustomLayer(id, visible) {
    setCustomLayers(prev => prev.map(l => l.id === id ? { ...l, visible } : l));
    if (!map.current) return;
    [`${id}-fill`, `${id}-line`, `${id}-circle`].forEach(lid => {
      if (map.current.getLayer(lid)) {
        map.current.setLayoutProperty(lid, 'visibility', visible ? 'visible' : 'none');
      }
    });
  }

  function removeCustomLayer(id) {
    removeCustomLayerFromMap(id);
    setCustomLayers(prev => prev.filter(l => l.id !== id));
  }

  const filteredPoints = useMemo(() => {
    if (!pointsData) return { type: 'FeatureCollection', features: [] };
    if (viewBy === "All") return filterPointsForAll(pointsData, activeConditions, columnBins);
    if (binColumns.includes(viewBy)) {
      return {
        ...pointsData,
        features: pointsData.features.filter(f =>
          activeConditions.includes(getFeatureBinLabel(f, viewBy))
        ),
      };
    }
    return {
      ...pointsData,
      features: pointsData.features.filter(f =>
        activeConditions.includes((f.properties[viewBy] || '').trim())
      ),
    };
  }, [pointsData, viewBy, activeConditions, columnBins]);
  filteredPointsRef.current = filteredPoints;

    useEffect(() => {
      if (map.current && map.current.getSource('points')) {
        map.current.getSource('points').setData(filteredPoints);
      }
    }, [filteredPoints]);

    useEffect(() => {
      if (!map.current || !map.current.getSource('selected-points')) return;
      map.current.getSource('selected-points').setData(
        selectedPoints || { type: 'FeatureCollection', features: [] }
      );
      if (map.current.getLayer('points-layer')) {
        const hasSelection = (selectedPoints?.features?.length ?? 0) > 0;
        map.current.setLayoutProperty('points-layer', 'visibility', hasSelection ? 'none' : 'visible');
      }
      if (map.current.getLayer('selected-points-layer')) {
        map.current.setPaintProperty(
          'selected-points-layer', 'circle-color',
          buildColorExpression(viewBy, columnBins, activeConditions)
        );
      }
    }, [selectedPoints, viewBy, columnBins, activeConditions]);



  const totalLocations = filteredPoints?.features?.length || 0;

  const handleBoxMouseDown = (e) => {
    if (!drawActive || selectMode !== 'box') return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    boxStartRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setBoxRect({ left: boxStartRef.current.x, top: boxStartRef.current.y, width: 0, height: 0 });
  };

  const handleBoxMouseMove = (e) => {
    if (!boxStartRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const s = boxStartRef.current;
    setBoxRect({ left: Math.min(s.x, x), top: Math.min(s.y, y), width: Math.abs(x - s.x), height: Math.abs(y - s.y) });
  };

  const handleBoxMouseUp = (e) => {
    if (!boxStartRef.current || !map.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x2 = e.clientX - rect.left;
    const y2 = e.clientY - rect.top;
    const s = boxStartRef.current;
    boxStartRef.current = null;
    setBoxRect(null);
    setDrawActive(false);

    if (Math.abs(x2 - s.x) < 4 || Math.abs(y2 - s.y) < 4) return;

    const ll1 = map.current.unproject([s.x, s.y]);
    const ll2 = map.current.unproject([x2, y2]);
    const minLng = Math.min(ll1.lng, ll2.lng);
    const maxLng = Math.max(ll1.lng, ll2.lng);
    const minLat = Math.min(ll1.lat, ll2.lat);
    const maxLat = Math.max(ll1.lat, ll2.lat);

    const candidates = [
      ...(filteredPointsRef.current?.features || []),
      ...(showSubstationsRef.current ? (substationsDataRef.current?.features || []).filter(f => f.geometry?.type === 'Point') : []),
      ...customLayersRef.current.flatMap(l => l.visible ? (l.geojson?.features || []).filter(f => f.geometry?.type === 'Point') : []),
    ];
    const inside = candidates.filter(f => {
      const [lng, lat] = f.geometry.coordinates;
      return lng >= minLng && lng <= maxLng && lat >= minLat && lat <= maxLat;
    });
    if (inside.length > 0) {
      setSelectedPoints({ type: 'FeatureCollection', features: inside });
    }
  };

  function hexToKmlAbgr(hex) {
    // Convert #RRGGBB to aabbggrr (KML uses alpha-blue-green-red)
    hex = hex.replace('#', '');
    if (hex.length !== 6) return 'ff000000';
    const r = hex.slice(0, 2);
    const g = hex.slice(2, 4);
    const b = hex.slice(4, 6);
    return `ff${b}${g}${r}`;

  }

  function geojsonToKML(geojson) {
    let kml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    kml += `<kml xmlns="http://www.opengis.net/kml/2.2"><Document>\n`;
    geojson.features.forEach(f => {
      let color = '#000000'; // default black
  
      // Figure out which color to use
      if (f.properties && f.properties['Slope Level'] && conditionColors['Slope Level'][f.properties['Slope Level']]) {
        color = conditionColors['Slope Level'][f.properties['Slope Level']];
      }
      if (f.properties && f.properties['Distance from Water Bodies(in km)'] && conditionColors['Distance from Water Bodies(in km)'][f.properties['Distance from Water Bodies(in km)']]) {
        color = conditionColors['Distance from Water Bodies(in km)'][f.properties['Distance from Water Bodies(in km)']];
      }
      if (f.properties && f.properties['LULC Scenarios'] && conditionColors['LULC Scenarios'][f.properties['LULC Scenarios']]) {
        color = conditionColors['LULC Scenarios'][f.properties['LULC Scenarios']];
      }
      if (f.properties && f.properties['Distance from Cities(in km)'] && conditionColors['Distance from Cities(in km)'][f.properties['Distance from Cities(in km)']]) {
        color = conditionColors['Distance from Cities(in km)'][f.properties['Distance from Cities(in km)']];
      }
      if (f.properties && f.properties['Distance from PT Lines(in km)'] && conditionColors['Distance from PT Lines(in km)'][f.properties['Distance from PT Lines(in km)']]) {
        color = conditionColors['Distance from PT Lines(in km)'][f.properties['Distance from PT Lines(in km)']];
      }
      if (f.properties && f.properties['Distance from Roads(in km)'] && conditionColors['Distance from Roads(in km)'][f.properties['Distance from Roads(in km)']]) {
        color = conditionColors['Distance from Roads(in km)'][f.properties['Distance from Roads(in km)']];
      if (viewBy === "Wind Speed(m/s)-100m") {
        const v = Number(f.properties["Wind Speed(m/s)-100m"]);
        const bins = columnBins["Wind Speed(m/s)-100m"] || [];
        bins.forEach((bin, idx) => {
          if (v >= bin.min && v < (bin.max ?? Infinity)) {
            color = windSpeedColors[idx % windSpeedColors.length];
          }
        });
      }
      }

      const kmlColor = hexToKmlAbgr(color);
  
      kml += `<Placemark>\n`;
      kml += `<name>${f.properties.name || ''}</name>\n`;
      kml += `<Style><IconStyle><color>${kmlColor}</color></IconStyle></Style>\n`;

      const props = { ...f.properties, "Latitude": f.geometry.coordinates[1], "Longitude": f.geometry.coordinates[0] };
      const propLines = TOOLTIP_FIELDS.map(h => `${h}: ${props[h] ?? ''}`).join('<br/>');
      kml += `<description><![CDATA[${propLines}]]></description>\n`;
      if (f.geometry.type === "Point") {
        kml += `<Point><coordinates>${f.geometry.coordinates[0]},${f.geometry.coordinates[1]},0</coordinates></Point>\n`;
      }
      kml += `</Placemark>\n`;
    });
    kml += `</Document></kml>`;
    return kml;}

  const TOOLTIP_FIELDS = [
    "name",
    "Latitude",
    "Longitude",
    "Slope Level",
    "Slope score",
    "Distance from Water Bodies(in km)",
    "WaterBody score",
    "LULC Scenarios",
    "LULC score",
    "Distance from Cities(in km)",
    "City score",
    "Distance from PT Lines(in km)",
    "PT Line score",
    "Distance from Roads(in km)",
    "Road score",
    "Quantitative Score",
    "Qualitative Score",
    "Final Score",
    "Wind Speed(m/s)-100m",
    "LCOE Values"
  ];
  
  
  function downloadKML() {
    const kmlStr = geojsonToKML(filteredPoints);
    const blob = new Blob([kmlStr], { type: 'application/vnd.google-earth.kml+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'filtered_points.kml';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  }

  function normalizeSlopeLevel(val) {
    if (!val) return "NA";
    // Force all to string and trim
    let s = String(val).trim();
    // Handle exact bins
    const bins = ["<5", "5-10", "10-15", "15-25", ">25"];
    if (bins.includes(s)) return s;
  
    // Date/Month format catchers (Excel weirdness)
    if (s.match(/oct/i) || s.match(/10.?15/i)) return "10-15";
    if (s.match(/sep/i) || s.match(/5-10/i) || s.match(/05-10/i)) return "5-10";
    if (s.match(/nov/i) || s.match(/15-25/i) || s.match(/15.?25/i)) return "15-25";
    if (s.match(/>25/)) return ">25";
    if (s.match(/<5/)) return "<5";
  
    // If it looks like "05-Oct" or "05/Oct", treat as "10-15"
    if (s.match(/^0?5[\/\- ]?oct/i)) return "10-15";
  
    // Numeric only values (rare)
    if (s === "5") return "<5";
    if (s === "10") return "5-10";
    if (s === "15") return "10-15";
    if (s === "25") return "15-25";
    if (parseFloat(s) > 25) return ">25";
  
    return "NA";
  }
  
  function downloadCSV() {
    if (!filteredPoints?.features || filteredPoints.features.length === 0) return;
    const headers = TOOLTIP_FIELDS;
    const rows = filteredPoints.features.map(f => {
      const props = { ...f.properties };
      props["Slope Level"] = "'" + normalizeSlopeLevel(String(props["Slope Level"]));
      props["Latitude"] = f.geometry.coordinates[1];
      props["Longitude"] = f.geometry.coordinates[0];
      return headers.map(h => JSON.stringify(props[h] ?? "")).join(",");
    });
    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'filtered_points.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  function getConditionKeysForFeatures(features, columnBins) {
    const keys = new Set();
    features.forEach(f => {
      // For categorical conditions
      Object.keys(conditionColors).forEach(condType => {
        const val = f.properties[condType];
        if (val && val !== "NA") keys.add(`${condType}:${val}`);
      });
      // For binned numeric columns
      binColumns.forEach(col => {
        const v = Number(f.properties[col]);
        const bins = columnBins[col] || [];
        const bin = bins.find(b => v >= b.min && v < (b.max ?? Infinity));
        if (bin) keys.add(`${col}:${bin.label}`);
      });
    });
    return Array.from(keys);
  }

  const selectedCount = selectedPoints?.features?.length || 0;
  const inspectorProps = inspectorFeature?.properties || {};
  const inspectorCoords = inspectorFeature?.geometry?.coordinates || [];

  const selectionSummary = useMemo(() => {
    const features = selectedPoints?.features;
    if (!features?.length) return null;
    const cols = [
      { key: 'Wind Speed(m/s)-100m', label: 'Wind Speed', unit: 'm/s', dp: 2 },
      { key: 'Final Score',          label: 'Final Score', unit: '',    dp: 3 },
      { key: 'LCOE Values',          label: 'LCOE',        unit: '',    dp: 3 },
      { key: 'Qualitative Score',    label: 'Qual. Score', unit: '',    dp: 3 },
      { key: 'Quantitative Score',   label: 'Quant. Score',unit: '',    dp: 3 },
    ];
    return cols.map(({ key, label, unit, dp }) => {
      const vals = features.map(f => Number(f.properties[key])).filter(v => isFinite(v));
      if (!vals.length) return null;
      const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
      return { label, unit, dp, avg, min: Math.min(...vals), max: Math.max(...vals) };
    }).filter(Boolean);
  }, [selectedPoints]);

  useEffect(() => {
    if (selectedPoints?.features?.length > 0) setShowSummary(true);
    else setShowSummary(false);
  }, [selectedPoints]);

  if (
    viewBy === "Wind Speed(m/s)-100m" &&
    (!columnBins["Wind Speed(m/s)-100m"] || columnBins["Wind Speed(m/s)-100m"].length === 0)
  ) {
    return <div className="mv-loading">Loading wind speed data…</div>;
  }

  return (
    <div className={`mv-workspace${leftPanelCollapsed ? ' layer-collapsed' : ''}${inspectorOpen ? ' inspector-open' : ''}`}>
      <ProgressBar progress={progress} visible={progressVisible} label={loadingLabel} />
      <div className="mv-appbar">
        <div className="mv-appbar-left">
          <img src={tawanaiLogo} alt="Tawanai" className="mv-header-logo" />
          <div className="mv-brand-lockup">
            <div className="mv-header-title">Algeria Technical Assistance</div>
            <div className="mv-appbar-subtitle">Wind resource suitability workspace</div>
          </div>
          <select
            value={region}
            onChange={e => setRegion(e.target.value)}
            className="mv-region-select"
          >
            <option value="Global Algeria">Global Algeria</option>
            <option value="Northern Algeria">Northern Algeria</option>
          </select>
        </div>

        <form onSubmit={handleLocIdSearch} className="mv-appbar-search">
          <FaSearch className="mv-search-icon" />
          <input
            type="text"
            placeholder="Search Loc ID"
            value={searchLocId}
            onChange={e => setSearchLocId(e.target.value)}
            className="mv-search-input"
          />
          <button type="submit" className="mv-search-btn">Go</button>
        </form>

        <div className="mv-appbar-right">
          <div className="mv-appbar-metric">
            <span className="mv-metric-label">Sites</span>
            <span className="mv-metric-value">{totalLocations}</span>
          </div>
          <button
            className={`mv-icon-btn${inspectorOpen ? ' active' : ''}`}
            onClick={() => setInspectorOpen(prev => !prev)}
            aria-label={inspectorOpen ? 'Hide feature inspector' : 'Show feature inspector'}
            title={inspectorOpen ? 'Hide feature inspector' : 'Show feature inspector'}
          >
            <FaInfoCircle />
          </button>
          {firstName && (
            <span className="mv-greeting">Hi {firstName}</span>
          )}
          <button
            className="mv-profile-btn"
            onClick={() => setShowProfileModal(true)}
            aria-label="Show profile"
          >
            <FaUserCircle size={28} color="#94a3b8" />
          </button>
        </div>
      </div>
              <div className="mv-map-tools">
                <div className="mv-basemap-switcher">
                  {[
                    { label: 'Dark', style: 'mapbox://styles/mapbox/dark-v11' },
                    { label: 'Streets', style: 'mapbox://styles/mapbox/streets-v12' },
                    { label: 'Satellite', style: 'mapbox://styles/mapbox/satellite-streets-v12' },
                  ].map(s => (
                    <button
                      key={s.style}
                      className={`mv-basemap-btn${mapStyle === s.style ? ' active' : ''}`}
                      onClick={() => setMapStyle(s.style)}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <button
                  className="mv-recenter-round"
                  title="Re-center map"
                  onClick={() => { if (map.current) map.current.flyTo(region === 'Global Algeria' ? { center: [2.0, 28.5], zoom: 4.39, essential: true } : { center: [4.0435, 34.8659], zoom: 6.56, essential: true }); }}
                >
                  <FaLocationArrow style={{ transform: 'rotate(-90deg)' }} />
                </button>
              </div>
              {/* Side Panel */}
              <div
                className={`mv-sidebar mv-layer-panel${leftPanelCollapsed ? ' collapsed' : ''}`}
              >
                <div className="mv-sidebar-accent" />
              <div className="mv-panel-tabs">
                {(leftPanelCollapsed || panelTab === 'layers') && (
                  <button
                    className={`mv-panel-tab${panelTab === 'layers' && !leftPanelCollapsed ? ' active' : ''}`}
                    onClick={() => {
                      if (leftPanelCollapsed) { setLeftPanelCollapsed(false); setPanelTab('layers'); }
                      else { setLeftPanelCollapsed(true); }
                    }}
                  >
                    Layers
                  </button>
                )}
                {(leftPanelCollapsed || panelTab === 'scoring') && (
                  <button
                    className={`mv-panel-tab${panelTab === 'scoring' && !leftPanelCollapsed ? ' active' : ''}`}
                    onClick={() => {
                      if (leftPanelCollapsed) { setLeftPanelCollapsed(false); setPanelTab('scoring'); }
                      else { setLeftPanelCollapsed(true); }
                    }}
                  >
                    Weights
                  </button>
                )}
                {(leftPanelCollapsed || panelTab === 'tools') && (
                  <button
                    className={`mv-panel-tab${panelTab === 'tools' && !leftPanelCollapsed ? ' active' : ''}`}
                    onClick={() => {
                      if (leftPanelCollapsed) { setLeftPanelCollapsed(false); setPanelTab('tools'); }
                      else { setLeftPanelCollapsed(true); }
                    }}
                  >
                    Tools
                  </button>
                )}
                {(leftPanelCollapsed || panelTab === 'guide') && (
                  <button
                    className={`mv-panel-tab${panelTab === 'guide' && !leftPanelCollapsed ? ' active' : ''}`}
                    onClick={() => {
                      if (leftPanelCollapsed) { setLeftPanelCollapsed(false); setPanelTab('guide'); }
                      else { setLeftPanelCollapsed(true); }
                    }}
                  >
                    Guide
                  </button>
                )}
              </div>

              {panelTab === 'layers' && (<>
              <div style={{ marginBottom: '4px' }}>
                  <label className="mv-section-label">View By</label>
                  <select value={viewBy} onChange={(e) => setViewBy(e.target.value)} className="mv-view-select">
                    <option value="All">All sites</option>
                    <optgroup label="Wind Resource">
                      <option value="Wind Speed(m/s)-100m">Wind Speed 100m</option>
                    </optgroup>
                    <optgroup label="Scores">
                      <option value="Qualitative Score">Qualitative Score</option>
                      <option value="Quantitative Score">Quantitative Score</option>
                      <option value="LCOE Values">LCOE Values</option>
                      <option value="Final Score">Final Score</option>
                    </optgroup>
                    <optgroup label="Site Conditions">
                      <option value="Slope Level">Slope Level</option>
                      <option value="LULC Scenarios">LULC Scenarios</option>
                    </optgroup>
                    <optgroup label="Infrastructure Distances">
                      <option value="Distance from Water Bodies(in km)">Water Bodies (km)</option>
                      <option value="Distance from Cities(in km)">Cities (km)</option>
                      <option value="Distance from PT Lines(in km)">Power Lines (km)</option>
                      <option value="Distance from Roads(in km)">Roads (km)</option>
                    </optgroup>
                  </select>
                </div>
                <div style={{ marginTop: '10px' }}>
                  <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontWeight: 700,
                      marginBottom: '12px',
                      fontSize: '14px',
                      letterSpacing: '0.02em',
                      fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
                      color: '#202842'
                    }}>
                  </div>
                  {viewBy === "All" && (
                    <div className="mv-btn-row">
                      <button
                        className="mv-btn mv-btn-select"
                        onClick={() => {
                          const allKeys = getConditionKeysForFeatures(pointsData?.features || [], columnBins);
                          setActiveConditions(allKeys);
                        }}
                      >
                        Select All
                      </button>
                      <button className="mv-btn mv-btn-clear" onClick={() => setActiveConditions([])}>
                        Unselect All
                      </button>
                    </div>
                  )}
                  {viewBy !== "All" && (
                    <div className="mv-btn-row">
                      <button
                        className="mv-btn mv-btn-select"
                        onClick={() => {
                          let all;
                          if (conditionColors[viewBy]) {
                            all = Object.keys(conditionColors[viewBy]);
                          } else if (binColumns.includes(viewBy) && columnBins[viewBy]) {
                            all = columnBins[viewBy].map(bin => bin.label);
                          } else {
                            all = [];
                          }
                          setActiveConditions(all);
                        }}
                      >
                        Select All
                      </button>
                      <button className="mv-btn mv-btn-clear" onClick={() => setActiveConditions([])}>
                        Unselect All
                      </button>
                    </div>
                  )}
                  {viewBy === "All" ? (
                      <>
                        {/* Categorical sections */}
                        {Object.keys(conditionColors).map((condType) => (
                          <React.Fragment key={condType}>
                            <div className="mv-group-header">{condType}</div>
                            {Object.entries(conditionColors[condType]).map(([cond, color]) => {
                              const condKey = `${condType}:${cond}`;
                              const checked = activeConditions.includes(condKey);
                              const allPossibleValues = new Set(pointsData?.features?.map(f => f.properties[condType]) ?? []);
                              const isValid = allPossibleValues.has(cond);
                              return (
                                <div
                                  key={`${condType}-${cond}`}
                                  className={`mv-cond-row${checked ? '' : ' inactive'}`}
                                >
                                  <div className="mv-cond-left">
                                    <input
                                      type="checkbox"
                                      className="mv-checkbox"
                                      checked={checked}
                                      disabled={!isValid}
                                      onChange={e => {
                                        const condKey = `${condType}:${cond}`;
                                        let newActive;
                                        if (e.target.checked) {
                                          newActive = Array.from(new Set([...activeConditions, condKey]));
                                        } else {
                                          newActive = activeConditions.filter(c => c !== condKey);
                                        }
                                        const filteredFeatures = filterPointsForAll(pointsData, newActive, columnBins).features;
                                        const keysInFeatures = getConditionKeysForFeatures(filteredFeatures, columnBins);
                                        setActiveConditions(keysInFeatures);
                                      }}
                                    />
                                    <div className="mv-swatch" style={{ backgroundColor: color }} />
                                    <span className="mv-cond-label">{cond}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </React.Fragment>
                        ))}

                        {/* Numeric bins section */}
                        {binColumns.map(col => (
                          <React.Fragment key={col}>
                            <div className="mv-group-header">{col}</div>
                            {(columnBins[col] || []).map((bin, idx) => {
                              const isValid = pointsData?.features?.some(f => {
                                const v = Number(f.properties[col]);
                                return v >= bin.min && v < (bin.max ?? Infinity);
                              });
                              const binKey = `${col}:${bin.label}`;
                              const checked = activeConditions.includes(binKey);
                              const swatchColor = col === "Wind Speed(m/s)-100m"
                                ? windSpeedColors[idx % windSpeedColors.length]
                                : binColors[idx % binColors.length];
                              return (
                                <div
                                  key={binKey}
                                  className={`mv-cond-row${checked ? '' : ' inactive'}`}
                                >
                                  <div className="mv-cond-left">
                                    <input
                                      type="checkbox"
                                      className="mv-checkbox"
                                      checked={checked}
                                      disabled={!isValid}
                                      onChange={e => {
                                        let newActive;
                                        if (e.target.checked) {
                                          newActive = Array.from(new Set([...activeConditions, binKey]));
                                        } else {
                                          newActive = activeConditions.filter(c => c !== binKey);
                                        }
                                        const filteredFeatures = filterPointsForAll(pointsData, newActive, columnBins).features;
                                        const keysInFeatures = getConditionKeysForFeatures(filteredFeatures, columnBins);
                                        setActiveConditions(keysInFeatures);
                                      }}
                                    />
                                    <div className="mv-swatch" style={{ backgroundColor: swatchColor }} />
                                    <span className="mv-cond-label">{bin.label}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </>
                    ) : (
                      <>
                      {conditionColors[viewBy] && (
                        <div className="mv-group-header">{viewBy}</div>
                      )}
                      {(conditionColors[viewBy] ? Object.entries(conditionColors[viewBy]) : []).map(([cond, color]) => {
                        const isValid = pointsData?.features?.some(f => {
                          const val = f.properties[viewBy];
                          return (typeof val === 'string' ? val.trim() : String(val)) === cond;
                        });
                        const checked = activeConditions.includes(cond);
                        return (
                          <div
                            key={cond}
                            className={`mv-cond-row${checked ? '' : ' inactive'}`}
                          >
                            <div className="mv-cond-left">
                              <input
                                type="checkbox"
                                className="mv-checkbox"
                                checked={checked}
                                disabled={!isValid}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setActiveConditions(prev => [...prev, cond]);
                                  } else {
                                    setActiveConditions(prev => prev.filter(c => c !== cond));
                                  }
                                }}
                              />
                              <div className="mv-swatch" style={{ backgroundColor: color }} />
                              <span className="mv-cond-label">{cond}</span>
                            </div>
                          </div>
                        );
                      })}
                  {binColumns.includes(viewBy) && columnBins[viewBy] && columnBins[viewBy].length > 0 && (
                    <>
                      <div className="mv-group-header">{viewBy}</div>
                      {columnBins[viewBy].map((bin, idx) => {
                        const isValid = pointsData?.features?.some(f => {
                          const v = Number(f.properties[viewBy]);
                          return !isNaN(v) && v >= bin.min && v < (bin.max ?? Infinity);
                        });
                        const color = viewBy === "Wind Speed(m/s)-100m"
                          ? windSpeedColors[idx % windSpeedColors.length]
                          : binColors[idx % binColors.length];
                        const checked = activeConditions.includes(bin.label);
                        return (
                          <div
                            key={bin.label}
                            className={`mv-cond-row${checked ? '' : ' inactive'}`}
                          >
                            <div className="mv-cond-left">
                              <input
                                type="checkbox"
                                className="mv-checkbox"
                                checked={checked}
                                disabled={!isValid}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setActiveConditions(prev => [...prev, bin.label]);
                                  } else {
                                    setActiveConditions(prev => prev.filter(c => c !== bin.label));
                                  }
                                }}
                              />
                              <div className="mv-swatch" style={{ backgroundColor: color }} />
                              <span className="mv-cond-label">{bin.label}</span>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                  </>
                )}
                  <button className="mv-btn-download mv-btn-download-kml" onClick={downloadKML}>
                    ↓ Download KML
                  </button>
                  <button className="mv-btn-download mv-btn-download-csv" onClick={downloadCSV}>
                    ↓ Download CSV
                  </button>
                  {selectedPoints && selectedPoints.features.length > 0 && (
                    <div className="mv-selected-panel">
                      <div className="mv-selected-header">
                        <span>{selectedPoints.features.length} selected</span>
                        <button className="mv-selected-clear" onClick={() => setSelectedPoints(null)}>✕</button>
                      </div>
                      <button className="mv-btn-download mv-btn-download-kml" style={{ marginTop: 8 }} onClick={() => {
                        const kmlStr = geojsonToKML(selectedPoints);
                        const blob = new Blob([kmlStr], { type: 'application/vnd.google-earth.kml+xml' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = 'selected_points.kml';
                        document.body.appendChild(a); a.click();
                        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
                      }}>↓ KML (selected)</button>
                      <button className="mv-btn-download mv-btn-download-csv" onClick={() => {
                        const headers = TOOLTIP_FIELDS;
                        const rows = selectedPoints.features.map(f => {
                          const props = { ...f.properties };
                          props["Slope Level"] = "'" + normalizeSlopeLevel(String(props["Slope Level"]));
                          props["Latitude"] = f.geometry.coordinates[1];
                          props["Longitude"] = f.geometry.coordinates[0];
                          return headers.map(h => JSON.stringify(props[h] ?? "")).join(",");
                        });
                        const csvContent = [headers.join(","), ...rows].join("\n");
                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = 'selected_points.csv';
                        document.body.appendChild(a); a.click();
                        document.body.removeChild(a); URL.revokeObjectURL(url);
                      }}>↓ CSV (selected)</button>
                    </div>
                  )}
              </div>
              </>)}

              {panelTab === 'scoring' && (
                <div className="mv-scoring-tab">
                  <p className="mv-scoring-desc">
                    Adjust the score assigned to each condition. Higher scores rank a site more favourably.
                  </p>
                  {Object.keys(conditionColors).map(condType => (
                    <React.Fragment key={condType}>
                      <div className="mv-group-header">{condType}</div>
                      {Object.entries(conditionColors[condType]).map(([cond, color]) => {
                        const isValid = pointsData?.features?.some(f => f.properties[condType] === cond);
                        return (
                          <div key={cond} className={`mv-cond-row${isValid ? '' : ' inactive'}`}>
                            <div className="mv-cond-left">
                              <div className="mv-swatch" style={{ backgroundColor: color }} />
                              <span className="mv-cond-label">{cond}</span>
                            </div>
                            <input
                              type="number"
                              min={0}
                              max={10}
                              className="mv-score-input"
                              disabled={!isValid}
                              value={scoringDraft[condType]?.[cond] ?? ''}
                              onChange={e => setScoringDraft(prev => ({
                                ...prev,
                                [condType]: { ...prev[condType], [cond]: Number(e.target.value) }
                              }))}
                            />
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                  <button
                    className="mv-btn-download mv-btn-download-kml"
                    style={{ marginTop: 24 }}
                    disabled={scoringSaving}
                    onClick={async () => {
                      setScoringsSaving(true);
                      setScoringMsg('');
                      try {
                        const changes = [];
                        for (const [condType, conds] of Object.entries(scoringDraft)) {
                          for (const [cond, score] of Object.entries(conds)) {
                            if (scoreMap[condType]?.[cond] !== score) {
                              changes.push({ 'Condition Type': condType, 'Condition': cond, 'Score': score });
                            }
                          }
                        }
                        if (changes.length > 0) {
                          await axios.post(
                            `${API_URL}/api/update-scores-batch/?region=${encodeURIComponent(region)}`,
                            { updates: changes },
                            axiosConfig
                          );
                        }
                        const refreshed = await axios.get(`${API_URL}/api/points/?region=${encodeURIComponent(region)}`, axiosConfig);
                        setPointsData(refreshed.data);
                        map.current.getSource('points').setData(refreshed.data);
                        setScoringMsg('Saved');
                      } catch {
                        setScoringMsg('Failed to save');
                      }
                      setScoringsSaving(false);
                    }}
                  >
                    {scoringSaving ? 'Saving…' : '✓ Save Changes'}
                  </button>
                  {scoringMsg && (
                    <div style={{ marginTop: 8, fontSize: 11, textAlign: 'center', color: scoringMsg === 'Saved' ? '#10b981' : '#f87171' }}>
                      {scoringMsg}
                    </div>
                  )}
                </div>
              )}

              {panelTab === 'tools' && (
                <div className="mv-tools-tab">
                  <div className="mv-group-header">Point Selection</div>
                  <div className="mv-tool-btns">
                    <button
                      className={`mv-tool-btn${selectMode === 'polygon' && drawActive ? ' active' : ''}`}
                      onClick={() => {
                        if (!drawRef.current) return;
                        if (drawActive && selectMode === 'polygon') {
                          drawRef.current.deleteAll();
                          drawRef.current.changeMode('simple_select');
                          setDrawActive(false);
                        } else {
                          if (drawActive && selectMode === 'box') { setDrawActive(false); setBoxRect(null); boxStartRef.current = null; }
                          setSelectMode('polygon');
                          drawRef.current.changeMode('draw_polygon');
                          setDrawActive(true);
                        }
                      }}
                    >
                      <FaDrawPolygon />
                      <span>Polygon</span>
                    </button>
                    <button
                      className={`mv-tool-btn${selectMode === 'box' && drawActive ? ' active' : ''}`}
                      onClick={() => {
                        if (drawActive && selectMode === 'box') {
                          setDrawActive(false); setBoxRect(null); boxStartRef.current = null;
                        } else {
                          if (drawRef.current) { drawRef.current.deleteAll(); drawRef.current.changeMode('simple_select'); }
                          setSelectMode('box');
                          setDrawActive(true);
                        }
                      }}
                    >
                      <FaRegSquare />
                      <span>Box</span>
                    </button>
                    <button
                      className={`mv-tool-btn${selectMode === 'distance' && drawActive ? ' active' : ''}`}
                      onClick={() => {
                        if (drawActive && selectMode === 'distance') {
                          setDrawActive(false);
                        } else {
                          if (drawRef.current) { drawRef.current.deleteAll(); drawRef.current.changeMode('simple_select'); }
                          setDrawActive(false); setBoxRect(null); boxStartRef.current = null;
                          setDistancePoints([null, null]);
                          setSelectMode('distance');
                          setDrawActive(true);
                        }
                      }}
                    >
                      <FaRulerHorizontal />
                      <span>Distance</span>
                    </button>
                  </div>
                  {selectMode === 'distance' && drawActive && (
                    <div className="mv-distance-result">
                      {!distancePoints[0] && <span className="mv-distance-hint">Click point A on the map</span>}
                      {distancePoints[0] && !distancePoints[1] && <span className="mv-distance-hint">Click point B on the map</span>}
                      {distanceKm !== null && (
                        <div className="mv-distance-value">
                          <span className="mv-distance-num">{distanceKm >= 1 ? distanceKm.toFixed(2) : (distanceKm * 1000).toFixed(0)}</span>
                          <span className="mv-distance-unit">{distanceKm >= 1 ? 'km' : 'm'}</span>
                          <button className="mv-distance-reset" onClick={() => setDistancePoints([null, null])} title="Measure again">↺</button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mv-group-header" style={{ marginTop: 20 }}>Overlay Layers</div>
                  <div className="mv-cond-row" style={{ paddingTop: 4, paddingBottom: 4 }}>
                    <div className="mv-cond-left">
                      <input
                        type="checkbox"
                        className="mv-checkbox"
                        checked={showSubstations}
                        onChange={e => setShowSubstations(e.target.checked)}
                      />
                      <div style={{ background: 'linear-gradient(180deg,#FCD34D,#F59E0B)', borderRadius: 3, width: 18, height: 12, border: '1px solid rgba(0,0,0,0.15)', display: 'inline-block', flexShrink: 0 }} />
                      <span className="mv-cond-label">
                        Substations
                        {substationsStatus === 'loading' && <span style={{ color: '#94a3b8', fontSize: 10, marginLeft: 4 }}>loading…</span>}
                        {substationsStatus === 'error' && <span style={{ color: '#f87171', fontSize: 10, marginLeft: 4 }}>failed</span>}
                        {substationsStatus === 'idle' && substationsData && <span style={{ color: '#94a3b8', fontSize: 10, marginLeft: 4 }}>({substationsData.features?.length ?? 0})</span>}
                      </span>
                    </div>
                  </div>

                  <div className="mv-group-header" style={{ marginTop: 20 }}>Upload Overlay</div>
                  <label
                    htmlFor="custom-layer-upload"
                    className="mv-upload-label"
                  >
                    <span style={{ fontSize: 15, lineHeight: 1 }}>+</span> Upload KML / KMZ
                  </label>
                  <input
                    id="custom-layer-upload"
                    type="file"
                    accept=".kml,.kmz"
                    style={{ display: 'none' }}
                    onChange={handleCustomLayerUpload}
                  />
                  {customLayers.map(layer => (
                    <div key={layer.id} className="mv-cond-row" style={{ paddingTop: 4, paddingBottom: 4 }}>
                      <div className="mv-cond-left" style={{ flex: 1, minWidth: 0 }}>
                        <input
                          type="checkbox"
                          className="mv-checkbox"
                          checked={layer.visible}
                          onChange={e => toggleCustomLayer(layer.id, e.target.checked)}
                        />
                        <div style={{ background: layer.color, borderRadius: 3, width: 18, height: 12, border: '1px solid rgba(0,0,0,0.15)', display: 'inline-block', flexShrink: 0 }} />
                        <span className="mv-cond-label" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 90 }}>
                          {layer.name}
                        </span>
                      </div>
                      <button
                        onClick={() => removeCustomLayer(layer.id)}
                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 13, padding: '0 2px', lineHeight: 1, flexShrink: 0 }}
                        title="Remove layer"
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}

              {panelTab === 'guide' && (
                <div className="mv-guide-tab">
                  <div className="mv-guide-lang-toggle">
                    <button className={`mv-guide-lang-btn${guideLang === 'en' ? ' active' : ''}`} onClick={() => setGuideLang('en')}>EN</button>
                    <button className={`mv-guide-lang-btn${guideLang === 'fr' ? ' active' : ''}`} onClick={() => setGuideLang('fr')}>FR</button>
                  </div>

                  {guideLang === 'en' ? (
                    <>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Overview</div>
                        <p className="mv-guide-text">This tool helps analysts identify optimal wind farm sites across Algeria. Each point on the map represents a 1 km² candidate location scored on wind resource, infrastructure proximity, and land-use suitability.</p>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Map Navigation</div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Pan</span><span className="mv-guide-val">Click and drag the map</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Zoom</span><span className="mv-guide-val">Scroll wheel or pinch</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Re-center</span><span className="mv-guide-val">Location arrow button (bottom-left)</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Basemap</span><span className="mv-guide-val">Street / Satellite / Terrain switcher (bottom-left)</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Search</span><span className="mv-guide-val">Type a place name in the top search bar and press Go</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Region</span><span className="mv-guide-val">Switch to and from Northern Algeria</span></div>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Layers Tab</div>
                        <div className="mv-guide-item"><span className="mv-guide-key">View By</span><span className="mv-guide-val">Color-codes map points by a chosen metric — Final Score, Wind Speed, LCOE, etc.</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Legend</span><span className="mv-guide-val">Colored swatches show which value range or category each color represents</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Filters</span><span className="mv-guide-val">Uncheck any condition to hide those points from the map; re-check to restore them</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Select All / Unselect All</span><span className="mv-guide-val">Bulk-toggle all conditions in one click</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Export KML</span><span className="mv-guide-val">Download visible shortlisted points as a KML file (Google Earth compatible)</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Export CSV</span><span className="mv-guide-val">Download the same selection as a spreadsheet</span></div>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Weights Tab</div>
                        <p className="mv-guide-text">Each row is a scoring criterion with a weight (out of 10) assigned per category. For example, a slope under 5° might be given a weight of 10 (most favourable) while a slope over 25° gets 0. Changing a weight and saving rewrites those values in the database and triggers a full score recalculation.</p>
                        <div className="mv-guide-subsection-title">How scores roll up to Final Score</div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Qualitative Score</span><span className="mv-guide-val">Derived from terrain and land-use criteria — slope, water bodies, cities, PT lines, roads, and LULC. The five non-LULC scores are summed and normalised; LULC is weighted separately and combined to produce the Qualitative Score.</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Quantitative Score</span><span className="mv-guide-val">Derived from wind speed and LCOE data. This value is fixed per location and is not affected by weight edits.</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Final Score</span><span className="mv-guide-val">A weighted combination of the Qualitative Score and the Quantitative Score. The Quantitative Score carries more weight, so wind resource quality has the strongest influence on the Final Score.</span></div>
                        <div className="mv-guide-subsection-title">Editing weights</div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Edit weight</span><span className="mv-guide-val">Type a new value (0–10) directly into the number field next to any condition</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Save Changes</span><span className="mv-guide-val">Click "✓ Save Changes" to write all edits to the database and refresh the map with recalculated scores</span></div>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Tools Tab</div>
                        <div className="mv-guide-subsection-title">Polygon Select</div>
                        <p className="mv-guide-text">Click Draw to activate, then click on the map to place polygon vertices. Double-click to close the shape. All points inside are shortlisted.</p>
                        <div className="mv-guide-subsection-title">Box Select</div>
                        <p className="mv-guide-text">Click Draw to activate, then click and drag on the map to draw a rectangle. All points inside are shortlisted.</p>
                        <div className="mv-guide-subsection-title">After Shortlisting</div>
                        <p className="mv-guide-text">A floating badge appears showing the count. Click ▴ to open a summary table with Avg / Min / Max for key metrics. Click ✕ on the badge to clear the selection.</p>
                        <div className="mv-guide-subsection-title">Distance Tool</div>
                        <p className="mv-guide-text">Click Draw to activate, then click two points on the map. Markers labeled A and B appear with a dashed line between them. The straight-line (great-circle) distance in km is shown in the panel. Click ↺ to reset.</p>
                        <div className="mv-guide-subsection-title">Substations</div>
                        <p className="mv-guide-text">Toggle the checkbox to show or hide power-grid substation markers on the map.</p>
                        <div className="mv-guide-subsection-title">Upload KML / KMZ</div>
                        <p className="mv-guide-text">Click the upload area to import a KML or KMZ file as a custom overlay. Uploaded layers appear in the list below and can be removed individually with ✕.</p>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Feature Inspector</div>
                        <p className="mv-guide-text">Click any point on the map to open the Feature Inspector on the right. It shows the full data for that location: Wind Speed, Final Score, LCOE, Qualitative Score, Quantitative Score, distance to substations, road proximity, land use classification, and coordinates. Close with ✕.</p>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Clicking Points</div>
                        <p className="mv-guide-text">Zoom in until the hint "Click on a point to view detailed information" appears at the bottom of the map. Then click any point to open the Feature Inspector with full data for that location.</p>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Creating a New Account</div>
                        <p className="mv-guide-text">Access is invite-only. To register a new account, you will need an invite code from the Tawanai team.</p>
                        <div className="mv-guide-item"><span className="mv-guide-key">Step 1</span><span className="mv-guide-val">On the login page, click "Don't have an account? Sign up"</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Step 2</span><span className="mv-guide-val">Fill in your First Name, Email, Username, and Password</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Step 3</span><span className="mv-guide-val">Enter the Invite Code provided to you and click "Create account"</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">After registration</span><span className="mv-guide-val">Sign in with your username and password — the invite code is not needed again</span></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Aperçu</div>
                        <p className="mv-guide-text">Cet outil aide les analystes à identifier les sites optimaux pour les parcs éoliens en Algérie. Chaque point sur la carte représente un emplacement candidat de 1 km² évalué selon la ressource éolienne, la proximité des infrastructures et l'adéquation de l'utilisation des terres.</p>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Navigation sur la carte</div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Panoramique</span><span className="mv-guide-val">Cliquer et faire glisser la carte</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Zoom</span><span className="mv-guide-val">Molette de défilement ou pincement</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Recentrer</span><span className="mv-guide-val">Bouton flèche de localisation (en bas à gauche)</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Fond de carte</span><span className="mv-guide-val">Sélecteur Rue / Satellite / Terrain (en bas à gauche)</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Recherche</span><span className="mv-guide-val">Saisir un nom de lieu dans la barre de recherche en haut et appuyer sur Aller</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Région</span><span className="mv-guide-val">Basculer vers ou depuis le nord de l'Algérie</span></div>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Onglet Couches</div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Afficher par</span><span className="mv-guide-val">Code couleur des points selon une métrique — Score Final, Vitesse du vent, LCOE, etc.</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Légende</span><span className="mv-guide-val">Les échantillons de couleur indiquent la plage de valeurs ou la catégorie représentée</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Filtres</span><span className="mv-guide-val">Décocher une condition pour masquer ces points ; recocher pour les restaurer</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Tout sélectionner / Désélectionner</span><span className="mv-guide-val">Activer ou désactiver toutes les conditions en un clic</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Exporter KML</span><span className="mv-guide-val">Télécharger les points présélectionnés visibles en fichier KML (compatible Google Earth)</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Exporter CSV</span><span className="mv-guide-val">Télécharger la même sélection sous forme de tableur</span></div>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Onglet Pondérations</div>
                        <p className="mv-guide-text">Chaque ligne est un critère de notation avec une pondération (sur 10) attribuée par catégorie. Par exemple, une pente inférieure à 5° peut recevoir une pondération de 10 (la plus favorable) tandis qu'une pente supérieure à 25° obtient 0. Modifier une pondération et sauvegarder réécrit ces valeurs dans la base de données et déclenche un recalcul complet des scores.</p>
                        <div className="mv-guide-subsection-title">Calcul du Score Final</div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Score Qualitatif</span><span className="mv-guide-val">Dérivé des critères de terrain et d'utilisation des terres — pente, plans d'eau, villes, lignes HT, routes et LULC. Les cinq scores hors LULC sont sommés et normalisés ; le LULC est pondéré séparément pour produire le Score Qualitatif.</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Score Quantitatif</span><span className="mv-guide-val">Dérivé des données de vitesse du vent et de LCOE. Cette valeur est fixe par emplacement et n'est pas affectée par les modifications de pondération.</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Score Final</span><span className="mv-guide-val">Combinaison pondérée du Score Qualitatif et du Score Quantitatif. Le Score Quantitatif a plus de poids, donc la qualité de la ressource éolienne influence davantage le Score Final.</span></div>
                        <div className="mv-guide-subsection-title">Modifier les pondérations</div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Modifier</span><span className="mv-guide-val">Saisir directement une nouvelle valeur (0–10) dans le champ numérique à côté de la condition</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Enregistrer</span><span className="mv-guide-val">Cliquer sur "✓ Enregistrer" pour sauvegarder toutes les modifications et rafraîchir la carte avec les scores recalculés</span></div>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Onglet Outils</div>
                        <div className="mv-guide-subsection-title">Sélection polygonale</div>
                        <p className="mv-guide-text">Cliquer sur Dessiner pour activer, puis cliquer sur la carte pour placer les sommets du polygone. Double-cliquer pour fermer la forme. Tous les points à l'intérieur sont présélectionnés.</p>
                        <div className="mv-guide-subsection-title">Sélection rectangulaire</div>
                        <p className="mv-guide-text">Cliquer sur Dessiner pour activer, puis cliquer et faire glisser pour tracer un rectangle. Tous les points à l'intérieur sont présélectionnés.</p>
                        <div className="mv-guide-subsection-title">Après la présélection</div>
                        <p className="mv-guide-text">Un badge flottant affiche le nombre de points. Cliquer sur ▴ pour ouvrir un tableau récapitulatif avec Moy / Min / Max. Cliquer sur ✕ pour effacer la sélection.</p>
                        <div className="mv-guide-subsection-title">Outil de distance</div>
                        <p className="mv-guide-text">Cliquer sur Dessiner pour activer, puis cliquer sur deux points de la carte. Des marqueurs A et B apparaissent avec une ligne pointillée. La distance orthodromique en km est affichée dans le panneau. Cliquer sur ↺ pour réinitialiser.</p>
                        <div className="mv-guide-subsection-title">Postes électriques</div>
                        <p className="mv-guide-text">Cocher ou décocher la case pour afficher ou masquer les marqueurs de postes du réseau électrique.</p>
                        <div className="mv-guide-subsection-title">Importer KML / KMZ</div>
                        <p className="mv-guide-text">Cliquer sur la zone d'importation pour charger un fichier KML ou KMZ en superposition. Les couches importées apparaissent dans la liste et peuvent être supprimées individuellement avec ✕.</p>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Inspecteur de données</div>
                        <p className="mv-guide-text">Cliquer sur un point de la carte pour ouvrir l'Inspecteur de données à droite. Il affiche toutes les données de l'emplacement : Vitesse du vent, Score Final, LCOE, Score Qualitatif, Score Quantitatif, distance aux postes, proximité des routes, classification de l'utilisation des terres et coordonnées. Fermer avec ✕.</p>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Cliquer sur les points</div>
                        <p className="mv-guide-text">Zoomez jusqu'à ce que le message "Cliquez sur un point pour afficher les informations détaillées" apparaisse en bas de la carte. Cliquez ensuite sur un point pour ouvrir l'Inspecteur de données.</p>
                      </div>
                      <div className="mv-guide-section">
                        <div className="mv-guide-section-title">Créer un nouveau compte</div>
                        <p className="mv-guide-text">L'accès est sur invitation uniquement. Pour créer un compte, vous aurez besoin d'un code d'invitation de l'équipe Tawanai.</p>
                        <div className="mv-guide-item"><span className="mv-guide-key">Étape 1</span><span className="mv-guide-val">Sur la page de connexion, cliquer sur "Vous n'avez pas de compte ? S'inscrire"</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Étape 2</span><span className="mv-guide-val">Remplir votre Prénom, E-mail, Nom d'utilisateur et Mot de passe</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Étape 3</span><span className="mv-guide-val">Saisir le Code d'invitation fourni et cliquer sur "Créer un compte"</span></div>
                        <div className="mv-guide-item"><span className="mv-guide-key">Après l'inscription</span><span className="mv-guide-val">Se connecter avec votre nom d'utilisateur et mot de passe — le code d'invitation n'est plus nécessaire</span></div>
                      </div>
                    </>
                  )}
                </div>
              )}

              </div>
              {/* Map */}
              <div
                ref={mapContainer}
                className="mv-map-canvas"
              />
              {mapStatus.zoom >= 7 && (
                <div className="mv-click-hint">
                  Click on a point to view detailed information
                </div>
              )}
              {selectedPoints?.features?.length > 0 && (
                <div className="mv-selection-ui">
                  {showSummary && selectionSummary && (
                    <div className="mv-summary-card">
                      <div className="mv-summary-accent" />
                      <div className="mv-summary-header">
                        <span>{selectedPoints.features.length} sites selected</span>
                        <button onClick={() => setShowSummary(false)} title="Dismiss">✕</button>
                      </div>
                      <table className="mv-summary-table">
                        <thead>
                          <tr>
                            <th></th>
                            <th>Avg</th>
                            <th>Min</th>
                            <th>Max</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectionSummary.map(s => (
                            <tr key={s.label}>
                              <td className="mv-summary-row-label">{s.label}{s.unit && <span className="mv-summary-unit"> {s.unit}</span>}</td>
                              <td>{s.avg.toFixed(s.dp)}</td>
                              <td>{s.min.toFixed(s.dp)}</td>
                              <td>{s.max.toFixed(s.dp)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="mv-selection-badge">
                    <span>{selectedPoints.features.length} selected</span>
                    <button onClick={() => setShowSummary(p => !p)} title="Toggle summary">
                      {showSummary ? '▾' : '▴'}
                    </button>
                    <button onClick={() => setSelectedPoints(null)} title="Clear selection">✕</button>
                  </div>
                </div>
              )}
              {selectMode === 'box' && drawActive && (
                <div
                  className="mv-box-select-canvas"
                  onMouseDown={handleBoxMouseDown}
                  onMouseMove={handleBoxMouseMove}
                  onMouseUp={handleBoxMouseUp}
                  onMouseLeave={handleBoxMouseUp}
                >
                  {boxRect && (
                    <div
                      className="mv-box-select-rect"
                      style={{ left: boxRect.left, top: boxRect.top, width: boxRect.width, height: boxRect.height }}
                    />
                  )}
                </div>
              )}
              <div className="mv-statusbar">
                <span>Zoom {mapStatus.zoom.toFixed(2)}</span>
                <span>Lat {mapStatus.lat.toFixed(4)}</span>
                <span>Lng {mapStatus.lng.toFixed(4)}</span>
                <span>Scale {mapStatus.scale}</span>
                <span>Selected {selectedCount}</span>
                <span>CRS EPSG:4326</span>
                <span>Projection Web Mercator</span>
              </div>
              {inspectorOpen && (
                <aside className="mv-inspector" aria-label="Feature inspector">
                  <div className="mv-inspector-header">
                    <div>
                      <div className="mv-inspector-kicker">Feature Inspector</div>
                      <div className="mv-inspector-title">{inspectorProps.name || 'No site selected'}</div>
                    </div>
                    <button
                      className="mv-panel-close"
                      onClick={() => setInspectorOpen(false)}
                      aria-label="Close feature inspector"
                    >
                      <FaTimes />
                    </button>
                  </div>
                  {inspectorFeature ? (
                    <>
                      <div className="mv-inspector-metrics">
                        <div className="mv-inspector-metric">
                          <span>Wind</span>
                          <strong>{formatInspectorValue(inspectorProps["Wind Speed(m/s)-100m"])} m/s</strong>
                        </div>
                        <div className="mv-inspector-metric">
                          <span>Final</span>
                          <strong>{formatInspectorValue(inspectorProps["Final Score"])}</strong>
                        </div>
                        <div className="mv-inspector-metric">
                          <span>LCOE</span>
                          <strong>{formatInspectorValue(inspectorProps["LCOE Values"])}</strong>
                        </div>
                      </div>
                      <div className="mv-inspector-section">
                        <div className="mv-inspector-section-title">Location</div>
                        <div className="mv-inspector-row"><span>Latitude</span><strong>{formatInspectorValue(inspectorCoords[1], 5)}</strong></div>
                        <div className="mv-inspector-row"><span>Longitude</span><strong>{formatInspectorValue(inspectorCoords[0], 5)}</strong></div>
                        <div className="mv-inspector-row"><span>Land Use</span><strong>{formatInspectorValue(inspectorProps["LULC Scenarios"], 0)}</strong></div>
                        <div className="mv-inspector-row"><span>Slope</span><strong>{formatInspectorValue(inspectorProps["Slope Level"], 0)}</strong></div>
                      </div>
                      <div className="mv-inspector-section">
                        <div className="mv-inspector-section-title">Infrastructure</div>
                        <div className="mv-inspector-row"><span>Water Bodies</span><strong>{formatInspectorValue(inspectorProps["Distance from Water Bodies(in km)"], 0)}</strong></div>
                        <div className="mv-inspector-row"><span>Cities</span><strong>{formatInspectorValue(inspectorProps["Distance from Cities(in km)"], 0)}</strong></div>
                        <div className="mv-inspector-row"><span>PT Lines</span><strong>{formatInspectorValue(inspectorProps["Distance from PT Lines(in km)"], 0)}</strong></div>
                        <div className="mv-inspector-row"><span>Roads</span><strong>{formatInspectorValue(inspectorProps["Distance from Roads(in km)"], 0)}</strong></div>
                      </div>
                      <div className="mv-inspector-section">
                        <div className="mv-inspector-section-title">Scores</div>
                        <div className="mv-inspector-row"><span>Quantitative</span><strong>{formatInspectorValue(inspectorProps["Quantitative Score"])}</strong></div>
                        <div className="mv-inspector-row"><span>Qualitative</span><strong>{formatInspectorValue(inspectorProps["Qualitative Score"])}</strong></div>
                      </div>
                    </>
                  ) : (
                    <div className="mv-inspector-empty">Click a site on the map to inspect its wind, score, infrastructure, and land-use attributes.</div>
                  )}
                </aside>
              )}
              {/* Profile Modal */}
              {showProfileModal && (
                <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  background: 'rgba(15,23,42,0.35)',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                  zIndex: 3000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => setShowProfileModal(false)}
              >
                <div
                  style={{
                    background: '#fff',
                    borderRadius: 14,
                    boxShadow: '0 8px 40px rgba(0,0,0,0.14)',
                    border: '1px solid #e8edf3',
                    minWidth: 340,
                    padding: '36px 36px 28px 36px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                    fontFamily: "'Inter', -apple-system, sans-serif",
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <FaUserCircle size={48} color="#1e3a8a" style={{ marginBottom: 12 }} />
                  <div style={{ fontWeight: 800, fontSize: 22, color: '#0f172a', marginBottom: 4, letterSpacing: '-0.02em' }}>
                    {firstName ? `Hi ${firstName}` : ''}
                  </div>
                  <div style={{ fontWeight: 500, color: '#64748b', fontSize: 14, marginBottom: 24 }}>
                    {userName}
                  </div>
                  <button
                    style={{
                      width: '100%',
                      padding: '13px',
                      background: 'linear-gradient(135deg, #1a2f6e, #1e3a8a)',
                      color: '#fff',
                      fontWeight: 700,
                      border: 'none',
                      borderRadius: '10px',
                      marginBottom: 10,
                      cursor: 'pointer',
                      fontSize: '15px',
                      boxShadow: '0 2px 14px rgba(30,58,138,0.35)',
                      transition: 'all 0.2s',
                      fontFamily: "'Inter', -apple-system, sans-serif",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(30,58,138,0.5)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 14px rgba(30,58,138,0.35)'; e.currentTarget.style.transform = 'none'; }}
                    onClick={() => { setShowProfileModal(false); setShowEditProfileModal(true); }}
                  >
                    Edit Profile
                  </button>
                  <button
                    style={{
                      width: '100%',
                      padding: '13px',
                      background: '#f1f5f9',
                      color: '#dc2626',
                      fontWeight: 700,
                      border: '1.5px solid #fecaca',
                      borderRadius: '10px',
                      marginBottom: 10,
                      cursor: 'pointer',
                      fontSize: '15px',
                      transition: 'all 0.15s',
                      fontFamily: "'Inter', -apple-system, sans-serif",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                    onClick={() => { setShowProfileModal(false); if (onLogout) onLogout(); }}
                  >
                    Sign out
                  </button>
                  <button
                    style={{
                      background: 'transparent',
                      color: '#64748b',
                      border: 'none',
                      fontSize: 14,
                      cursor: 'pointer',
                      fontWeight: 500,
                      fontFamily: "'Inter', -apple-system, sans-serif",
                    }}
                    onClick={() => setShowProfileModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
            
            {showEditProfileModal && (
              <EditProfileModal
                onClose={() => setShowEditProfileModal(false)}
                onProfileUpdated={(first, user) => {
                  setFirstName(first);
                  setUserName(user);
                }}
                onLogout={onLogout}
              />
            )}
          </div>
        );
        };

        export default MapView;
